/**
 * StrategyBuilder — schema-v2 refactor, Phase-2 layout.
 *
 * Produces the following POST payload (schema_version: "2.0"):
 * {
 *   schema_version, strategy_meta, sections,
 *   global_rules, risk, execution, ui_state
 * }
 *
 * Phase-2 changes (layout only — no schema/API changes):
 * - Sticky top toolbar with back button, BuilderModeToggle, Cancel, Save
 * - Two-column layout: form (flex-1) + sticky preview panel (360px, desktop only)
 * - Mobile: single column with collapsible preview accordion above form
 * - Progressive description field: "+ Add note" → inline textarea expand
 * - BuilderModeToggle: Visual (active) / AI Assist (disabled, coming soon)
 *
 * AI-mode entry point (future): call hydrateFromV2Payload(generatedPayload)
 * to populate form state + sectionTrees, then buildLivePreview() renders preview.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { Zap, ChevronDown, Info, ArrowLeft, Plus, CheckCircle2, Circle, Copy, Check } from 'lucide-react';
import {
  useCreateStrategyV2,
  useUpdateStrategyV2,
  useStrategy,
  useStrategyConditionCatalog,
  useEnableMasterStrategy,
} from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { strategiesApi } from '../../api/client';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';
import { cn, symbolShort } from '../../lib/utils';
import { Spinner } from '../../components/ui';
import SymbolPickerModal from '../../components/SymbolPickerModal';
import ConditionTreeEditor, { deriveEvalTF } from './ConditionTreeEditor';
import {
  formSchemaV2,
  DEFAULTS_V2,
  buildSchemaV2Payload,
  buildManualStrategyPayload,
  emptyGroup,
  attachIds,
  mapExecutionLegToUiLeg,
  TRADE_IN_NEXT_EXP_OPTIONS,
  normalizeTradeInNextExp,
  isPartialExitToggleEnabled,
  isPyramidingToggleEnabled,
  resolveAllowPartialExit,
} from './schemaV2';
import {
  buildLivePreview,
  deriveRuleLabel,
  deriveSecondaryStrip,
} from '../../lib/strategyPreview';
import { generateSectionExplanation } from '../../lib/strategyExplainer';
import { resolveBuilderEntry } from './guidedCreation';
import { isTradingViewExternal, showOptionsByAlertLotNote } from './tradingViewSetup';
import { TradingViewJsonActions, TradingViewGuideContent } from './TradingViewSetupGuide';
import BottomSheet from '../../components/ui/BottomSheet';
import {
  shouldAttemptAutoReservation,
  extractReservedToken,
  isTokenReservationUnsupportedError,
} from './strategyTokenReservation';

// ─── UI Atoms ──────────────────────────────────────────────────────────────────

function Field({ label, error, hint, required, children }) {
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-1 field-label">
        {label}{required && <span className="text-amber/80">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-2xs font-mono text-txt-muted leading-relaxed">{hint}</p>}
      {error && <p className="text-2xs font-mono text-bear leading-relaxed">{error}</p>}
    </div>
  );
}

function FInput({ className, ...props }) {
  return <input className={cn('input-base', className)} {...props} />;
}

function FSelect({ options, className, ...props }) {
  return (
    <div className="relative">
      <select className={cn('input-base appearance-none pr-7 cursor-pointer', className)} {...props}>
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = typeof o === 'string' ? o : o.label;
          const dis = typeof o === 'object' ? (o.disabled ?? false) : false;
          return (
            <option key={val} value={val} disabled={dis}>{lbl}</option>
          );
        })}
      </select>
      <ChevronDown size={11} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none" />
    </div>
  );
}

function Section({ title, hint, accent, children, headerRight, compact }) {
  return (
    <div className={cn('panel', compact ? 'p-3 space-y-2' : 'p-4 space-y-4')}>
      <div className="flex items-center gap-2.5 border-b border-bg-border/60 pb-2.5">
        {accent && <span className={cn('w-0.5 h-4 rounded-full shrink-0', accent)} />}
        <h3 className="section-title">{title}</h3>
        {hint && (
          <div className="group relative ml-0.5">
            <Info size={11} className="text-txt-muted cursor-help" />
            <div className="absolute left-full ml-2 top-0 hidden group-hover:block bg-bg-card border border-bg-border rounded-lg px-2.5 py-1.5 text-2xs text-txt-secondary whitespace-nowrap z-10 shadow-panel">
              {hint}
            </div>
          </div>
        )}
        {headerRight && <div className="ml-auto">{headerRight}</div>}
      </div>
      {children}
    </div>
  );
}

function ToggleField({ label, checked, onChange, hint }) {
  return (
    <label className={cn(
      'flex items-start gap-2.5 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors duration-150',
      checked
        ? 'border-amber/30 bg-amber/5'
        : 'border-bg-border bg-bg-surface/40 hover:border-bg-hover'
    )}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 accent-amber w-4 h-4 shrink-0"
      />
      <div>
        <div className={cn('text-xs font-medium', checked ? 'text-txt-primary' : 'text-txt-secondary')}>{label}</div>
        {hint && <div className="text-2xs text-txt-muted font-mono mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}

// ─── Inline leg row (label left · input right) ────────────────────────────────

function LegRow({ label, children }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-2xs font-mono text-txt-label shrink-0 whitespace-nowrap">{label}</span>
      {children}
    </div>
  );
}

// ─── Builder Mode Toggle ────────────────────────────────────────────────────────

function BuilderModeToggle() {
  return (
    <div className="flex items-center gap-0.5 bg-bg-surface border border-bg-border rounded-lg p-0.5">
      <button
        type="button"
        className="px-3 py-1 rounded text-xs font-medium bg-amber/10 text-amber border border-amber/25"
      >
        Visual
      </button>
      <button
        type="button"
        disabled
        title="AI Assist — coming soon"
        className="px-3 py-1 rounded text-xs font-medium text-txt-muted opacity-50 cursor-not-allowed"
      >
        AI Assist
      </button>
    </div>
  );
}

// ─── Strategy Preview Panel ────────────────────────────────────────────────────

const SECTION_LABELS = {
  long_entry:  { label: 'Long Entry',  accent: 'bg-bull/70'  },
  long_exit:   { label: 'Long Exit',   accent: 'bg-bull/40'  },
  short_entry: { label: 'Short Entry', accent: 'bg-bear/70'  },
  short_exit:  { label: 'Short Exit',  accent: 'bg-bear/40'  },
};

function countConditions(tree) {
  if (!tree) return 0;
  let n = 0;
  const walk = (node) => {
    if (!node) return;
    if (node.node_type === 'condition') { n++; return; }
    for (const child of (node.children || [])) walk(child);
  };
  walk(tree);
  return n;
}

function StrategyPreviewPanel({ formValues, sectionTrees, optionMapping, strategySource, externalSourceType, manual }) {
  const isExternal   = strategySource === 'EXTERNAL';
  const isManual     = strategySource === 'MANUAL';
  const preview      = buildLivePreview(formValues, sectionTrees);
  const ruleLabel    = (isExternal || isManual) ? null : deriveRuleLabel(preview);
  const secondStrip  = (isExternal || isManual) ? null : deriveSecondaryStrip(preview);
  const name         = formValues?.name         || '';
  const symbol       = formValues?.symbol       || '';
  const timeframe    = formValues?.primary_timeframe || '';
  const instrument   = formValues?.execution?.instrument || 'UNDERLYING';

  const totalLegs = (optionMapping?.bullish?.length || 0) + (optionMapping?.bearish?.length || 0);
  const extSourceLabel = EXTERNAL_SOURCE_OPTIONS.find((o) => o.value === externalSourceType)?.label || externalSourceType || 'External';

  return (
    <div className="space-y-3">
      {/* Header card */}
      <div className="card p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-txt-primary truncate">
              {name || <span className="text-txt-muted italic">Strategy name</span>}
            </div>
          </div>
          {isManual ? (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded border border-amber/30 bg-amber/10 text-amber shrink-0">MAN</span>
          ) : isExternal ? (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded border border-info/30 bg-info/10 text-info shrink-0">EXT</span>
          ) : instrument === 'OPTIONS' ? (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded border border-warn/30 bg-warn/10 text-warn shrink-0">OPT</span>
          ) : (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded border border-bg-border text-txt-muted shrink-0">EQ</span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isManual && symbol && (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-bg-surface border border-bg-border text-txt-secondary">
              {symbolShort(symbol)}
            </span>
          )}
          {isManual && (manual?.exchange || manual?.segment) && (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-bg-surface border border-bg-border text-txt-secondary">
              {[manual?.exchange, manual?.segment].filter(Boolean).join(' · ')}
            </span>
          )}
          {!isExternal && !isManual && timeframe && (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-bg-surface border border-bg-border text-txt-secondary">
              {timeframe}
            </span>
          )}
          {isManual ? (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-amber/10 border border-amber/25 text-amber">
              {[manual?.tradeIn, manual?.qtyMode].filter(Boolean).join(' · ') || 'Manual'}
            </span>
          ) : isExternal ? (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-info/10 border border-info/25 text-info">
              {extSourceLabel}
            </span>
          ) : (
            ruleLabel !== '—' && (
              <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-amber/10 border border-amber/25 text-amber">
                {ruleLabel}
              </span>
            )
          )}
        </div>

        {!isExternal && secondStrip && (
          <div className="text-2xs font-mono text-txt-secondary border-t border-bg-border/50 pt-2 leading-relaxed">
            {secondStrip}
          </div>
        )}
      </div>

      {/* Sections status — internal only */}
      {isManual ? (
        <div className="card p-3">
          <div className="text-2xs font-mono text-txt-label uppercase tracking-widest mb-2">Signals</div>
          <p className="text-2xs font-mono text-amber">
            Manual BUY / SELL signals are generated from the Manual Trading watchlist after deployment.
          </p>
        </div>
      ) : isExternal ? (
        <div className="card p-3">
          <div className="text-2xs font-mono text-txt-label uppercase tracking-widest mb-2">Signals</div>
          <p className="text-2xs font-mono text-info">
            Signals generated externally via {extSourceLabel}.
          </p>
        </div>
      ) : (
        <div className="card p-3">
          <div className="text-2xs font-mono text-txt-label uppercase tracking-widest mb-2">Sections</div>
          <div className="space-y-1.5">
            {Object.entries(SECTION_LABELS).map(([key, { label, accent }]) => {
              const enabled = formValues?.sections?.[key]?.enabled ?? false;
              const count   = countConditions(sectionTrees?.[key]);
              return (
                <div key={key} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', enabled ? accent : 'bg-txt-muted/30')} />
                    <span className={cn('text-2xs font-mono', enabled ? 'text-txt-secondary' : 'text-txt-muted/50')}>
                      {label}
                    </span>
                  </div>
                  {enabled ? (
                    count > 0
                      ? <span className="text-2xs font-mono text-bull">{count} cond.</span>
                      : <span className="text-2xs font-mono text-warn">no conditions</span>
                  ) : (
                    <span className="text-2xs font-mono text-txt-muted/40">off</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Execution summary */}
      {instrument === 'OPTIONS' && totalLegs > 0 && (
        <div className="card p-3">
          <div className="text-2xs font-mono text-txt-label uppercase tracking-widest mb-2">Execution</div>
          <div className="flex items-center justify-between text-2xs font-mono">
            <span className="text-txt-secondary">Option legs</span>
            <span className="text-txt-primary">{totalLegs}</span>
          </div>
          <div className="flex items-center justify-between text-2xs font-mono mt-1">
            <span className="text-txt-secondary">Bullish / Bearish</span>
            <span className="text-txt-secondary">
              {optionMapping?.bullish?.length ?? 0} / {optionMapping?.bearish?.length ?? 0}
            </span>
          </div>
        </div>
      )}

      {/* Validation checklist */}
      <div className="card p-3">
        <div className="text-2xs font-mono text-txt-label uppercase tracking-widest mb-2">Checklist</div>
        <div className="space-y-1">
          {[
            { label: 'Strategy name',   ok: !!name.trim()      },
            ...(isManual
              ? [
                  { label: 'Exchange selected', ok: !!manual?.exchange },
                  { label: 'Segment selected',  ok: !!manual?.segment  },
                ]
              : [{ label: 'Symbol selected', ok: !!symbol }]),
            ...(!isExternal && !isManual ? [
              {
                label: 'At least one section enabled',
                ok: Object.keys(SECTION_LABELS).some(k => formValues?.sections?.[k]?.enabled),
              },
              {
                label: 'No empty sections',
                ok: Object.keys(SECTION_LABELS).every(k => {
                  const enabled = formValues?.sections?.[k]?.enabled;
                  return !enabled || countConditions(sectionTrees?.[k]) > 0;
                }),
              },
            ] : isExternal ? [
              { label: 'Signal provider selected', ok: true },
            ] : []),
          ].map(({ label, ok }) => (
            <div key={label} className="flex items-center gap-1.5">
              {ok
                ? <CheckCircle2 size={11} className="text-bull shrink-0" />
                : <Circle size={11} className="text-txt-muted/40 shrink-0" />}
              <span className={cn('text-2xs font-mono', ok ? 'text-txt-secondary' : 'text-txt-muted/50')}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Progressive Description Field ────────────────────────────────────────────

function DescriptionField({ register, watch, setValue, error }) {
  const value = watch('description') || '';
  const [editing, setEditing] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  if (!editing && !value) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors flex items-center gap-1"
        >
          <Plus size={10} /> Add note
        </button>
        {error && <p className="text-2xs font-mono text-bear leading-relaxed mt-1">{error}</p>}
      </div>
    );
  }

  if (!editing && value) {
    return (
      <div>
        <p className="text-xs font-mono text-txt-secondary line-clamp-2 leading-relaxed">{value}</p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors mt-1 underline underline-offset-2"
        >
          Edit
        </button>
        {error && <p className="text-2xs font-mono text-bear leading-relaxed mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <textarea
        ref={textareaRef}
        className="input-base h-[80px] resize-none"
        placeholder="Optional short description or note"
        {...register('description')}
        onBlur={() => setEditing(false)}
      />
      {error && <p className="text-2xs font-mono text-bear leading-relaxed">{error}</p>}
    </div>
  );
}

// ─── Symbol picker constants ───────────────────────────────────────────────────

// ─── Option mapping constants & helpers ───────────────────────────────────────

const UI_EXPIRY_OPTIONS = [
  { value: 'current_week',  label: 'Current Week'  },
  { value: 'next_week',     label: 'Next Week'     },
  { value: 'current_month', label: 'Current Month' },
  { value: 'next_month',    label: 'Next Month'    },
];

function normalizeUnderlyingCode(symbolLike) {
  if (!symbolLike) return '';
  const raw  = symbolLike.includes(':') ? symbolLike.split(':', 2)[1] : symbolLike;
  const base = raw.split('-', 1)[0].toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Exact family matching: 'NIFTY' or 'SENSEX' must be followed by a digit or end-of-string.
  // This accepts: NIFTY, NIFTY50, NIFTY25MAYFUT (Nifty 50 index / futures)
  // This rejects: NIFTYBANK, FINNIFTY, MIDCPNIFTY, NIFTYBEES — they do NOT map to 'NIFTY'.
  if (/^NIFTY(\d|$)/.test(base))  return 'NIFTY';
  if (/^SENSEX(\d|$)/.test(base)) return 'SENSEX';
  return base;
}

function normalizePreferredExecutionMode(value) {
  const token = String(value || '').trim().toUpperCase();
  if (token === 'LIVE') return 'LIVE';
  if (token === 'PAPER') return 'PAPER';
  return null;
}

function defaultUiExpiryForSymbol(symbol, underlyingSymbol = '', kind) {
  const baseCode       = normalizeUnderlyingCode(symbol);
  const underlyingCode = normalizeUnderlyingCode(underlyingSymbol);
  const isNiftySensex  =
    baseCode === 'NIFTY' || baseCode === 'SENSEX' ||
    underlyingCode === 'NIFTY' || underlyingCode === 'SENSEX';
  if (isNiftySensex && kind !== 'OPTION') return 'current_week';
  return 'current_month';
}

// Classify a symbol into OPTION | FUTURE | INDEX | STOCK.
// Priority: explicit instrument_type from API row > segment shortcut > symbol-pattern.
function classifySymbolKind(symbol, segment, instTypeFromRow) {
  if (instTypeFromRow) {
    const t = String(instTypeFromRow).toUpperCase();
    if (t === 'CE' || t === 'PE' || t === 'OPTION' || t === 'OPTIONS') return 'OPTION';
    if (t === 'FUT' || t === 'FUTURE' || t === 'FUTURES')              return 'FUTURE';
    if (t === 'INDEX' || t === 'IDX')                                  return 'INDEX';
  }
  if (segment === 'index') return 'INDEX';
  const bare = symbol ? (symbol.includes(':') ? symbol.split(':', 2)[1] : symbol) : '';
  if (/(?:CE|PE)$/i.test(bare)) return 'OPTION';
  if (/FUT$/i.test(bare))       return 'FUTURE';
  if (segment === 'eq' || segment === 'equity' || /\-EQ$/i.test(bare)) return 'STOCK';
  return 'STOCK';
}

// NIFTY / SENSEX index or future → all 4 expiry choices; everything else → monthly only.
// kind guards against accidentally offering weekly for NIFTY option contracts.
function getExpiryOptionsForSymbol(symbol, kind, strategySource = 'INTERNAL') {
  if (strategySource === 'EXTERNAL' || strategySource === 'MANUAL') return UI_EXPIRY_OPTIONS;
  const code = normalizeUnderlyingCode(symbol);
  const isNiftySensex = code === 'NIFTY' || code === 'SENSEX';
  if (isNiftySensex && kind !== 'OPTION') return UI_EXPIRY_OPTIONS;
  return UI_EXPIRY_OPTIONS.filter((o) => o.value === 'current_month' || o.value === 'next_month');
}

// Apply-on options:
//   Option Premium disabled when: non-options, multi-leg options, or multi-symbol
//   Points type disabled (separately) when: multi-symbol
function getApplyOnOptions(isOptionsInstrument, isMultiLeg, isMultiSymbol) {
  const optPremDisabled = !isOptionsInstrument || isMultiLeg || isMultiSymbol;
  return [
    { value: 'UNDERLYING',     label: 'Underlying'     },
    { value: 'OPTION_PREMIUM', label: 'Option Premium', disabled: optPremDisabled },
  ];
}

function createDefaultLeg(optionType, symbol, lotSize = 1, underlyingSymbol = '') {
  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
    action: 'BUY',
    optionType,
    expiry: defaultUiExpiryForSymbol(symbol, underlyingSymbol),
    strikeSelectionMode: 'manual',
    manualStrike: 'ATM',
    offset: 1,
    premium: '',
    lots: 1,
    lotSize: Math.max(1, Number(lotSize) || 1),
  };
}

// ─── RightDrawer (kept, no longer used for condition editor) ──────────────────

function RightDrawer({ open, title, onClose, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-2xl border-l border-bg-border bg-bg-base shadow-modal flex flex-col">
        <div className="px-5 py-4 flex items-center justify-between border-b border-bg-border shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-txt-primary">{title}</h3>
          </div>
          <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onClose}>✕ Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </aside>
    </div>,
    document.body
  );
}

// ─── Notes Modal ──────────────────────────────────────────────────────────────

function NotesModal({ open, value, onClose, onSave }) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { if (open) setDraft(value || ''); }, [open, value]);

  if (!open) return null;
  const modal = (
    <div className="fixed inset-0 z-[1100] bg-black/80 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="relative bg-bg-panel border border-bg-border rounded-xl w-full max-w-md shadow-modal flex flex-col animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-bg-border/60">
            <div>
              <h3 className="text-sm font-semibold text-txt-primary">Strategy Notes</h3>
              <p className="text-2xs font-mono text-txt-muted mt-0.5">Internal note — not shown to counterparties</p>
            </div>
            <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onClose}>✕</button>
          </div>
          <div className="p-4">
            <textarea
              className="input-base w-full h-[140px] resize-none"
              placeholder="Optional notes about this strategy, e.g. market conditions, rationale, review dates…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 px-4 pb-4">
            <button type="button" className="btn-ghost text-xs" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => { onSave(draft); onClose(); }}
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}


// ─── Section config ────────────────────────────────────────────────────────────

const SECTION_CONFIG = [
  { key: 'long_entry',  title: 'Long Entry',  accent: 'bg-bull/70',  hint: 'Conditions that trigger a long (buy) entry'  },
  { key: 'long_exit',   title: 'Long Exit',   accent: 'bg-bull/40',  hint: 'Conditions that trigger a long position exit' },
  { key: 'short_entry', title: 'Short Entry', accent: 'bg-bear/70',  hint: 'Conditions that trigger a short (sell) entry' },
  { key: 'short_exit',  title: 'Short Exit',  accent: 'bg-bear/40',  hint: 'Conditions that trigger a short position exit' },
];

const ENTRY_EXECUTION_MODES = [
  { value: 'IMMEDIATE',         label: 'Immediate'         },
  { value: 'NEXT_CANDLE_CLOSE', label: 'Next Candle Close' },
  { value: 'LTP_CROSS',         label: 'LTP Cross'         },
  { value: 'CROSS_AND_CLOSE',   label: 'Cross and Close'   },
];
const ENTRY_REFERENCE_OPTIONS = [
  { value: 'SETUP_CANDLE',    label: 'Setup Candle'    },
  { value: 'PREVIOUS_CANDLE', label: 'Previous Candle' },
];
const ENTRY_LEVEL_OPTIONS = [
  { value: 'HIGH',  label: 'High'  },
  { value: 'LOW',   label: 'Low'   },
  { value: 'CLOSE', label: 'Close' },
];
const ENTRY_VALIDITY_OPTIONS = [
  { value: 'UNTIL_TRIGGER',         label: 'Until Trigger'         },
  { value: 'EXPIRE_AFTER_N_CANDLES', label: 'Expire After N Candles' },
];
const ENTRY_REPLACEMENT_OPTIONS = [
  { value: 'REPLACE',    label: 'Replace with Latest'   },
  { value: 'KEEP_FIRST', label: 'Keep First Active'     },
];

const DEFAULT_ENTRY_EXECUTION = {
  mode:        'IMMEDIATE',
  reference:   'PREVIOUS_CANDLE',
  level:       'HIGH',
  buffer:      0,
  buffer_unit: 'points',
};
const DEFAULT_ENTRY_LIFECYCLE = {
  validity:       'UNTIL_TRIGGER',
  expiry_candles: 3,
  replacement:    'REPLACE',
};

function entryPreviewText(exec, lifecycle) {
  const ref  = exec.reference === 'SETUP_CANDLE' ? 'setup candle' : 'previous candle';
  const lvl  = (exec.level || 'HIGH').toLowerCase();
  const bufStr = (exec.buffer ?? 0) > 0
    ? ` + ${exec.buffer}${exec.buffer_unit === 'pct' ? '%' : ' pts'} buffer`
    : '';
  const execPart = {
    IMMEDIATE:         'enter immediately when the setup condition is met',
    NEXT_CANDLE_CLOSE: `enter after the next candle closes beyond the ${ref} ${lvl}`,
    LTP_CROSS:         `enter when LTP crosses the ${ref} ${lvl}${bufStr}`,
    CROSS_AND_CLOSE:   `enter after an intrabar cross and candle close above the ${ref} ${lvl}`,
  }[exec.mode] || 'enter when setup is met';
  const n        = Math.max(1, parseInt(lifecycle.expiry_candles, 10) || 3);
  const lifePart = lifecycle.validity === 'EXPIRE_AFTER_N_CANDLES'
    ? ` Setup expires if not triggered within ${n} candle${n !== 1 ? 's' : ''}.`
    : '';
  const replPart = lifecycle.replacement === 'KEEP_FIRST'
    ? ' The first active setup is kept until it triggers or expires.'
    : ' A newer valid setup replaces the previous one.';
  return `${execPart.charAt(0).toUpperCase()}${execPart.slice(1)}.${lifePart}${replPart}`;
}

// Compact inline select matching existing builder style
function EntrySelect({ label, value, options, onChange }) {
  return (
    <div>
      <div className="text-2xs font-mono text-txt-muted mb-0.5">{label}</div>
      <div className="relative">
        <select
          className="bg-bg-card border border-bg-border rounded px-2 py-1 text-2xs font-mono text-txt-primary appearance-none pr-5 cursor-pointer w-full"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <svg className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-txt-muted" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
          <path d="M5 7L1 3h8L5 7z" />
        </svg>
      </div>
    </div>
  );
}

// ─── Setup sentence helpers ────────────────────────────────────────────────────

const PRICE_TEXT_LABELS = {
  open:                 'Open',
  high:                 'High',
  low:                  'Low',
  close:                'Close',
  ltp:                  'LTP',
  volume:               'Volume',
  range:                'Range',
  body:                 'Body',
  upper_wick:           'Upper Wick',
  lower_wick:           'Lower Wick',
  body_pct_range:       'Body % of Range',
  upper_wick_pct_range: 'UW % of Range',
  lower_wick_pct_range: 'LW % of Range',
};

const SETUP_COMP_TEXT = {
  GT:          'is above',
  GTE:         'is above or equal to',
  LT:          'is below',
  LTE:         'is below or equal to',
  EQ:          'equals',
  CROSS_ABOVE: 'crosses above',
  CROSS_BELOW: 'crosses below',
};

function operandToText(op, fallbackTF) {
  if (!op) return '?';
  if (op.kind === 'constant') return String(op.value ?? '?');
  const tf     = op.timeframe || fallbackTF || '5m';
  const offset = parseInt(op.offset ?? 0, 10);
  const offStr = offset === 0 ? '(Latest)' : `(-${offset})`;
  if (op.source === 'price') {
    const isLTP = op.source === 'price' && op.name === 'ltp';
    const label = PRICE_TEXT_LABELS[op.name] || op.name;
    return isLTP ? 'LTP' : `${tf} ${label} ${offStr}`;
  }
  if (op.source === 'indicator') {
    const params = op.params ? Object.values(op.params).join(',') : '';
    const ind    = params ? `${op.name}(${params})` : op.name;
    const out    = op.output ? ` ${op.output}` : '';
    return `${tf} ${ind}${out} ${offStr}`;
  }
  return '?';
}

function conditionNodeToText(node, fallbackTF) {
  const l = operandToText(node.left,  fallbackTF);
  const r = operandToText(node.right, fallbackTF);
  const c = SETUP_COMP_TEXT[node.comparator] || node.comparator?.toLowerCase() || 'vs';
  return `${l} ${c} ${r}`;
}

function treeNodeToText(node, fallbackTF, isRoot) {
  if (!node) return null;
  if (node.node_type === 'condition') return conditionNodeToText(node, fallbackTF);
  if (node.node_type !== 'group') return null;
  const children = node.children || [];
  if (children.length === 0) return null;
  const parts = children.map(c => treeNodeToText(c, fallbackTF, false)).filter(Boolean);
  if (parts.length === 0) return null;
  const joined = parts.join(node.operator === 'OR' ? ' or ' : ' and ');
  return isRoot ? joined : `(${joined})`;
}

function setupTreeToReadableText(tree, fallbackTF) {
  if (!tree) return null;
  return treeNodeToText(tree, fallbackTF, true);
}

// ─── Summary panel components ──────────────────────────────────────────────────

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-2xs font-mono text-txt-muted/60 shrink-0 w-[72px]">{label}</span>
      <span className="text-2xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

function ExplanationBlock({ explanation }) {
  if (!explanation) {
    return (
      <p className="text-2xs font-mono text-txt-muted/40 italic">No conditions to explain yet.</p>
    );
  }
  return (
    <div className="space-y-0.5">
      <p className="text-2xs font-mono text-txt-secondary leading-snug mb-1">
        {explanation.intro}
      </p>
      {explanation.items.map((item, i) => {
        if (item.type === 'condition') return (
          <div key={i} className="flex gap-1 items-baseline">
            <span className="text-amber/50 shrink-0 select-none">•</span>
            <span className="text-2xs font-mono text-txt-secondary leading-snug">{item.text}</span>
          </div>
        );
        if (item.type === 'and') return (
          <div key={i} className="text-2xs font-mono text-txt-muted/50 pl-2">{item.text}</div>
        );
        if (item.type === 'spacer') return <div key={i} className="h-1.5" />;
        if (item.type === 'or_group') return (
          <div key={i}>
            <div className="flex gap-1 items-baseline">
              <span className="text-amber/50 shrink-0 select-none">•</span>
              <span className="text-2xs font-mono text-txt-secondary leading-snug">{item.label}</span>
            </div>
            {item.items.map((sub, j) => (
              <div key={j} className="flex gap-1 items-baseline pl-3.5">
                <span className="text-txt-muted/40 shrink-0 select-none">–</span>
                <span className="text-2xs font-mono text-txt-secondary leading-snug">{sub.text}</span>
              </div>
            ))}
          </div>
        );
        return null;
      })}
    </div>
  );
}

function EntrySummaryPanel({ exec, lc, tree, timeframe, sectionKey }) {
  const [lang, setLang] = useState('en');
  const explanation = generateSectionExplanation({ sectionKey, tree, execution: exec, lifecycle: lc, lang });

  return (
    <div className="rounded border border-bg-border/40 bg-bg-card/30 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-2xs font-mono text-txt-muted/70 font-semibold uppercase tracking-wide">Summary</p>
        <div className="flex rounded overflow-hidden border border-bg-border text-2xs font-mono">
          <button
            type="button"
            onClick={() => setLang('en')}
            className={cn(
              'px-1.5 py-0.5 transition-colors',
              lang === 'en' ? 'bg-amber/15 text-amber' : 'text-txt-muted hover:bg-bg-hover'
            )}
          >
            EN
          </button>
          <button
            type="button"
            onClick={() => setLang('hi')}
            className={cn(
              'px-1.5 py-0.5 transition-colors',
              lang === 'hi' ? 'bg-amber/15 text-amber' : 'text-txt-muted hover:bg-bg-hover'
            )}
          >
            हिंदी
          </button>
        </div>
      </div>
      <div className="rounded bg-info/5 border border-info/20 px-2.5 py-2">
        <ExplanationBlock explanation={explanation} />
      </div>
    </div>
  );
}

// ─── External Signal Source Panel ─────────────────────────────────────────────

const EXTERNAL_SOURCE_OPTIONS = [
  { value: 'TRADINGVIEW',      label: 'TradingView'        },
  { value: 'CHARTINK',         label: 'Chartink'           },
  { value: 'AMIBROKER',        label: 'Amibroker'          },
  { value: 'PYTHON',           label: 'Python'             },
];

const EXTERNAL_EXCHANGE_OPTIONS = [
  { value: 'NSE', label: 'NSE' },
  { value: 'BSE', label: 'BSE' },
  { value: 'MCX', label: 'MCX' },
];

const EXTERNAL_SEGMENT_OPTIONS = [
  { value: 'STOCK', label: 'Stock' },
  { value: 'FNO', label: 'F&O' },
  { value: 'INDEX', label: 'Index' },
  { value: 'COMMODITY', label: 'Commodity' },
  { value: 'ETF', label: 'ETF' },
];

const EXTERNAL_BROKER_OPTIONS = [
  { value: 'FYERS', label: 'FYERS' },
  { value: 'ZERODHA', label: 'Zerodha' },
  { value: 'NUVAMA', label: 'Nuvama' },
  { value: 'MASTERTRUST', label: 'MasterTrust' },
];

// ── Manual Strategy option sets — aligned with External Strategy execution ──
// (reuse EXTERNAL_EXCHANGE/SEGMENT for scope; product type mirrors External's
// instrument/segment-aware options; quantity mode stores the MODE only, no value)
const MANUAL_QTY_MODE_OPTIONS = [
  { value: 'FIXED_QTY', label: 'Fixed Qty' },
  { value: 'BY_MARGIN', label: 'Margin' },
];

function ExternalSignalSourcePanel({
  sourceType,
  onSourceTypeChange,
  externalExchange,
  onExternalExchangeChange,
  externalSegment,
  onExternalSegmentChange,
  externalBrokerName,
  onExternalBrokerNameChange,
  webhookUrl,
  webhookToken,
  tokenPending,
  tokenError,
  onRetryToken,
  providerLocked = false,
}) {
  const sourceLabel = EXTERNAL_SOURCE_OPTIONS.find((o) => o.value === sourceType)?.label || sourceType;
  const filteredSegmentOptions = useMemo(() => {
    if (externalExchange === 'MCX') return EXTERNAL_SEGMENT_OPTIONS.filter((o) => o.value === 'COMMODITY');
    return EXTERNAL_SEGMENT_OPTIONS.filter((o) => o.value !== 'COMMODITY');
  }, [externalExchange]);
  const [copiedField, setCopiedField] = useState(null);

  async function handleCopy(value, key) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(key);
      setTimeout(() => setCopiedField((prev) => (prev === key ? null : prev)), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }

  return (
    <Section title="External Signal Source" hint="Configure the external signal provider" accent="bg-info/50">
      <div className={`grid gap-2 ${sourceType === 'PYTHON' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Field label="Signal Provider">
          {providerLocked ? (
            // Guided creation fixed the provider on /strategies/create — render
            // a read-only value, not an editable dropdown (no Change Provider
            // control; Back returns to the guided page to switch).
            <div className="input-base flex items-center bg-bg-surface/50 text-txt-primary cursor-default" aria-readonly="true">
              {sourceLabel}
            </div>
          ) : (
            <FSelect
              className="w-full"
              options={EXTERNAL_SOURCE_OPTIONS}
              value={sourceType}
              onChange={(e) => onSourceTypeChange(e.target.value)}
            />
          )}
        </Field>
        <Field label="Exchange" required>
          <FSelect
            className="w-full"
            options={EXTERNAL_EXCHANGE_OPTIONS}
            value={externalExchange}
            onChange={(e) => {
              const ex = e.target.value;
              onExternalExchangeChange(ex);
              if (ex === 'MCX') onExternalSegmentChange('COMMODITY');
              else if (externalSegment === 'COMMODITY') onExternalSegmentChange('STOCK');
            }}
          />
        </Field>
        <Field label="Segment" required>
          <FSelect
            className="w-full"
            options={filteredSegmentOptions}
            value={externalSegment}
            onChange={(e) => onExternalSegmentChange(e.target.value)}
          />
        </Field>
        {sourceType === 'PYTHON' && (
          <Field label="Vendor" required>
            <FSelect
              className="w-full"
              options={EXTERNAL_BROKER_OPTIONS}
              value={externalBrokerName}
              onChange={(e) => onExternalBrokerNameChange(e.target.value)}
            />
          </Field>
        )}
      </div>
      <p className="text-2xs font-mono text-txt-muted">
        External strategies do not use Symbol Universe. ALGOSYS will inject a compiler-safe placeholder symbol internally from your exchange and segment settings.
      </p>
      <div className="rounded-lg border border-info/20 bg-info/5 px-3 py-2.5 space-y-1">
        <p className="text-xs font-medium text-info">External Signal Mode</p>
        <p className="text-2xs font-mono text-txt-secondary leading-relaxed">
          This strategy will receive signals from <span className="text-txt-primary">{sourceLabel}</span>. Signals are accepted only when the strategy has an active deployment.
        </p>
        {sourceType === 'PYTHON' && (
          <p className="text-2xs font-mono text-txt-muted leading-relaxed">
            Vendor means the source broker/data-provider symbol format used by your external signal generator. It does not control the user's live trading broker.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-2xs font-mono uppercase tracking-wide text-txt-muted">Webhook Endpoint</p>
              <button type="button" className="px-1.5 py-0.5 text-2xs flex items-center gap-1 rounded bg-info/20 text-info hover:bg-info/30" onClick={() => handleCopy(webhookUrl, 'url')}>
                {copiedField === 'url' ? <Check size={10} className="text-bull" /> : <Copy size={10} />}
                {copiedField === 'url' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-2xs font-mono text-txt-primary break-all">{webhookUrl}</p>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-2xs font-mono uppercase tracking-wide text-txt-muted">Strategy Token</p>
              {webhookToken && (
                <button type="button" className="px-1.5 py-0.5 text-2xs flex items-center gap-1 rounded bg-info/20 text-info hover:bg-info/30" onClick={() => handleCopy(webhookToken, 'token')}>
                  {copiedField === 'token' ? <Check size={10} className="text-bull" /> : <Copy size={10} />}
                  {copiedField === 'token' ? 'Copied' : 'Copy'}
                </button>
              )}
              {!webhookToken && !tokenPending && (
                <button type="button" className="btn-ghost px-1.5 py-0.5 text-2xs" onClick={onRetryToken}>
                  Reserve Now
                </button>
              )}
            </div>
            <p className="text-2xs font-mono text-txt-primary break-all">
              {webhookToken || (tokenPending ? 'Reserving token…' : tokenError ? 'Token reservation failed' : 'Available after save')}
            </p>
          </div>
        </div>
        {sourceType === 'TRADINGVIEW' && (
          // TradingView-only: plain-language JSON copy actions + sample Pine
          // Script download. AmiBroker/Python keep the functional URL/token
          // display above but get no TradingView content (their provider docs
          // come later).
          <TradingViewJsonActions webhookToken={webhookToken} />
        )}
      </div>
    </Section>
  );
}

const RISK_ROWS = [
  { key: 'target',        label: 'Target',        hint: 'Take profit target'          },
  { key: 'stop_loss',     label: 'Stop Loss',      hint: 'Maximum allowed loss'        },
  { key: 'trailing_stop', label: 'Trailing Stop',  hint: 'Dynamic stop follows price'  },
];

// ─── Preset Watchlists ────────────────────────────────────────────────────────
// TODO: In production, load from /api/strategy/symbol-universe/presets or expand server-side.
// Lists below are representative subsets; extend per index reconstitution.

const PRESET_WATCHLISTS = [
  {
    key: 'NIFTY_50', label: 'Nifty 50', segment: 'fno',
    symbols: [
      'NSE:RELIANCE-EQ', 'NSE:TCS-EQ', 'NSE:HDFCBANK-EQ', 'NSE:INFY-EQ', 'NSE:ICICIBANK-EQ',
      'NSE:HINDUNILVR-EQ', 'NSE:ITC-EQ', 'NSE:SBIN-EQ', 'NSE:KOTAKBANK-EQ', 'NSE:LT-EQ',
      'NSE:BAJFINANCE-EQ', 'NSE:AXISBANK-EQ', 'NSE:WIPRO-EQ', 'NSE:ASIANPAINT-EQ', 'NSE:MARUTI-EQ',
    ],
  },
  {
    key: 'NIFTY_100', label: 'Nifty 100', segment: 'fno',
    symbols: [
      'NSE:RELIANCE-EQ', 'NSE:TCS-EQ', 'NSE:HDFCBANK-EQ', 'NSE:INFY-EQ', 'NSE:ICICIBANK-EQ',
      'NSE:HINDUNILVR-EQ', 'NSE:ITC-EQ', 'NSE:SBIN-EQ', 'NSE:KOTAKBANK-EQ', 'NSE:LT-EQ',
      'NSE:BAJFINANCE-EQ', 'NSE:AXISBANK-EQ', 'NSE:WIPRO-EQ', 'NSE:TITAN-EQ', 'NSE:HCLTECH-EQ',
      'NSE:NESTLEIND-EQ', 'NSE:DMART-EQ', 'NSE:BAJAJFINSV-EQ', 'NSE:ULTRACEMCO-EQ', 'NSE:POWERGRID-EQ',
    ],
  },
  {
    key: 'NIFTY_FNO', label: 'Nifty FNO', segment: 'fno',
    symbols: [
      'NSE:RELIANCE-EQ', 'NSE:TCS-EQ', 'NSE:INFY-EQ', 'NSE:SBIN-EQ', 'NSE:AXISBANK-EQ',
      'NSE:TATAMOTORS-EQ', 'NSE:TATASTEEL-EQ', 'NSE:INDUSINDBK-EQ', 'NSE:ONGC-EQ', 'NSE:JSWSTEEL-EQ',
    ],
  },
];

// ─── Edit mode hydration helper ────────────────────────────────────────────────

export function hydrateFromV2Payload(cj) {
  const meta        = cj.strategy_meta || {};
  const exec        = cj.execution     || {};
  const sections    = cj.sections      || {};
  const gr          = cj.global_rules  || {};
  const r           = cj.risk          || {};
  const sc          = cj.ui_state?.scheduler || {};

  const rawEntryOrderType = String(exec.entry_order?.order_type || 'MARKET').toUpperCase();
  const hydratedOrderType = (
    rawEntryOrderType === 'SLM' ? 'SL-M'
    : rawEntryOrderType === 'SL' ? 'SL-L'
    : rawEntryOrderType
  );
  const formValues = {
    name:              meta.name              || '',
    description:       meta.description       || '',
    symbol:            meta.symbol            || '',
    primary_timeframe: meta.primary_timeframe || '5m',
    preferred_execution_mode: normalizePreferredExecutionMode(cj.ui_state?.preferred_execution_mode),
    global_rules: {
      trading_mode:             gr.trading_mode             || 'INTRADAY',
      start_time:               gr.start_time               || '09:15',
      end_time:                 gr.end_time                 || '15:20',
      no_entry_after:           gr.no_entry_after           || '15:00',
      no_trade_windows:         gr.no_trade_windows         || [],
      allow_long:               gr.allow_long               ?? true,
      allow_short:              gr.allow_short              ?? true,
      entry_conflict_policy:    gr.entry_conflict_policy    || 'SKIP_BOTH',
      opposite_signal_handling: gr.opposite_signal_handling || gr.opposite_signal_policy || 'EXIT_AND_REVERSE',
    },
    risk: {
      // enabled flag dropped from form state — value > 0 means enabled.
      // Backward compat: if saved strategy has enabled:false, zero out the value.
      target:        { type: r.target?.type        || 'percent', value: (r.target?.enabled        !== false && r.target?.value        > 0) ? r.target.value        : 0, apply_on: r.target?.apply_on        || 'UNDERLYING' },
      stop_loss:     { type: r.stop_loss?.type     || 'percent', value: (r.stop_loss?.enabled     !== false && r.stop_loss?.value     > 0) ? r.stop_loss.value     : 0, apply_on: r.stop_loss?.apply_on     || 'UNDERLYING' },
      trailing_stop: { type: r.trailing_stop?.type || 'percent', value: (r.trailing_stop?.enabled !== false && r.trailing_stop?.value > 0) ? r.trailing_stop.value : 0, apply_on: r.trailing_stop?.apply_on || 'UNDERLYING' },
    },
    execution: {
      instrument: exec.instrument || 'OPTIONS',
      allow_pyramiding: exec.allow_pyramiding ?? false,
      max_pyramid_legs: exec.max_pyramid_legs ?? 5,
      allow_partial_exit: resolveAllowPartialExit(exec.quantity, exec.allow_partial_exit),
      product_type: exec.product_type || null,
      trade_in_next_exp: normalizeTradeInNextExp(exec.trade_in_next_exp),
      entry_order: {
        order_type: ['MARKET', 'LIMIT', 'SL-M', 'SL-L'].includes(hydratedOrderType) ? hydratedOrderType : 'MARKET',
        mpp: exec.entry_order?.mpp ?? 2,
        price_mode: exec.entry_order?.price_mode || 'OFFSET_POINTS',
        limit_price_value: exec.entry_order?.limit_price_value ?? null,
        trigger_price_value: exec.entry_order?.trigger_price_value ?? null,
      },
      quantity: {
        mode: exec.quantity?.mode || 'FIXED_QTY',
        fixed_qty: exec.quantity?.fixed_qty ?? 1,
        margin_per_trade: exec.quantity?.margin_per_trade ?? null,
        risk_amount: exec.quantity?.risk_amount ?? null,
      },
    },
    sections: {
      long_entry:  { enabled: sections.long_entry?.enabled  ?? false },
      long_exit:   { enabled: sections.long_exit?.enabled   ?? false },
      short_entry: { enabled: sections.short_entry?.enabled ?? false },
      short_exit:  { enabled: sections.short_exit?.enabled  ?? false },
    },
    deploy_after_create: false,
    scheduler_enabled:   sc.enabled   ?? false,
    schedule_days_mask:  sc.days_mask ?? 31,
  };

  const sectionTrees = {};
  const sectionEntryConfigs = {};
  for (const key of ['long_entry', 'long_exit', 'short_entry', 'short_exit']) {
    const sec = sections[key];
    // Backward compat: new payloads store tree at setup.condition_tree; old at .tree
    const rawTree = sec?.setup?.condition_tree ?? sec?.tree ?? null;
    sectionTrees[key] = (sec?.enabled && rawTree) ? attachIds(rawTree) : null;

    if (key === 'long_entry' || key === 'short_entry') {
      const n = Math.max(1, parseInt(sec?.lifecycle?.expiry_candles, 10) || 3);
      sectionEntryConfigs[key] = {
        execution: {
          mode:        sec?.execution?.mode        || 'IMMEDIATE',
          reference:   sec?.execution?.reference   || 'PREVIOUS_CANDLE',
          level:       sec?.execution?.level       || 'HIGH',
          buffer:      sec?.execution?.buffer      ?? 0,
          buffer_unit: sec?.execution?.buffer_unit || 'points',
        },
        lifecycle: {
          validity:       sec?.lifecycle?.validity    || 'UNTIL_TRIGGER',
          expiry_candles: n,
          replacement:    sec?.lifecycle?.replacement || 'REPLACE',
        },
      };
    }
  }

  const bullishLegs = (exec.bullish_signal_legs || []).map(mapExecutionLegToUiLeg);
  const bearishLegs = (exec.bearish_signal_legs || []).map(mapExecutionLegToUiLeg);
  const firstLeg    = bullishLegs[0] || bearishLegs[0];
  const selectedLotSize = firstLeg?.lotSize || 1;

  // Hydrate symbol_universe — backward compat: fall back to strategy_meta.symbol as SINGLE_SYMBOL
  const su = cj.symbol_universe;
  let symbolUniverseHydrated;
  if (su?.symbols?.length > 0) {
    const primaryKind = su.mode === 'SINGLE_SYMBOL'
      ? classifySymbolKind(su.symbols[0], su.segment, null)
      : null;
    symbolUniverseHydrated = {
      mode:       su.mode       || 'SINGLE_SYMBOL',
      symbols:    su.symbols,
      segment:    su.segment    || null,
      kind:       primaryKind,
      preset_key: su.preset_key || null,
    };
  } else {
    const sym = meta.symbol || '';
    symbolUniverseHydrated = {
      mode: 'SINGLE_SYMBOL',
      symbols: sym ? [sym] : [],
      segment: null,
      kind: sym ? classifySymbolKind(sym, null, null) : null,
      preset_key: null,
    };
  }

  return {
    formValues,
    sectionTrees,
    sectionEntryConfigs,
    optionMapping: { bullish: bullishLegs, bearish: bearishLegs },
    selectedLotSize,
    symbolUniverse: symbolUniverseHydrated,
  };
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function StrategyBuilderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: strategyId } = useParams(); // route is /strategies/:id/edit
  const forUserId = new URLSearchParams(location.search).get('for_user');
  const isEdit = !!strategyId;
  // Guided creation entry (Phase 1). 'edit' and 'admin' (for_user) modes keep
  // every current behavior — guided params never override saved hydration or
  // the admin flow. 'guided' fixes type/provider for the creation session;
  // 'redirect' fails safe to /strategies/create (rendered after all hooks).
  const builderEntry = resolveBuilderEntry({ isEdit, forUserId, search: location.search });
  const isGuidedCreate = builderEntry.mode === 'guided';

  const [symbolPickerOpen, setSymbolPickerOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Symbol Universe — source of truth for all symbol selection modes
  const [symbolUniverse, setSymbolUniverse] = useState({
    mode: 'SINGLE_SYMBOL',
    symbols: DEFAULTS_V2.symbol ? [DEFAULTS_V2.symbol] : [],
    segment: null,
    kind: null,       // 'OPTION' | 'FUTURE' | 'INDEX' | 'STOCK' | null — single-symbol only
    preset_key: null,
  });
  const [selectedLotSize, setSelectedLotSize] = useState(DEFAULTS_V2.execution?.lot_size || 1);
  const [optionMapping, setOptionMapping] = useState(() => ({
    bullish: [createDefaultLeg('CE', DEFAULTS_V2.symbol, 1)],
    bearish: [createDefaultLeg('PE', DEFAULTS_V2.symbol, 1)],
  }));
  const [hydrated, setHydrated] = useState(false);

  // ── Strategy source (INTERNAL | EXTERNAL) ──────────────────────────────────
  // Phase 2 hook point: strategy_source and externalSourceType will be included
  // in the save payload once backend migration 013 is applied.
  // Guided creation initializes (and locks) these from the guided selection;
  // edit/admin modes keep the existing defaults + hydration behavior.
  const [strategySource, setStrategySource] = useState(
    () => (isGuidedCreate ? builderEntry.strategySource : 'INTERNAL')
  );
  const [externalMode, setExternalMode] = useState('SIMPLE');
  const [externalSourceType, setExternalSourceType] = useState(
    () => (isGuidedCreate && builderEntry.externalSourceType ? builderEntry.externalSourceType : 'TRADINGVIEW')
  );
  const [externalExchange, setExternalExchange] = useState('NSE');
  const [externalSegment, setExternalSegment] = useState('STOCK');
  const [externalBrokerName, setExternalBrokerName] = useState('FYERS');
  const [reservedStrategyToken, setReservedStrategyToken] = useState('');

  // ── Manual strategy state (strategy-level defaults only; per-symbol qty/risk
  //    live in the Manual Trading watchlist, NOT here) ──
  const [manualExchange, setManualExchange] = useState('NSE');
  const [manualSegment, setManualSegment] = useState('FNO');
  const [manualTradeIn, setManualTradeIn] = useState('OPTION');
  const [manualProductType, setManualProductType] = useState('INTRADAY');
  const [manualQtyMode, setManualQtyMode] = useState('FIXED_QTY');
  const [manualPyramiding, setManualPyramiding] = useState(false);
  const [manualTradeInNextExp, setManualTradeInNextExp] = useState('OFF');

  // Section trees live outside react-hook-form (complex nested objects)
  const [sectionTrees, setSectionTrees] = useState({
    long_entry:  null,
    long_exit:   null,
    short_entry: null,
    short_exit:  null,
  });

  // Entry execution + lifecycle config (entry sections only)
  const [sectionEntryConfig, setSectionEntryConfig] = useState({
    long_entry:  { execution: { ...DEFAULT_ENTRY_EXECUTION }, lifecycle: { ...DEFAULT_ENTRY_LIFECYCLE } },
    short_entry: { execution: { ...DEFAULT_ENTRY_EXECUTION }, lifecycle: { ...DEFAULT_ENTRY_LIFECYCLE } },
  });
  function updateEntryConfig(key, block, patch) {
    setSectionEntryConfig((prev) => ({
      ...prev,
      [key]: { ...prev[key], [block]: { ...prev[key][block], ...patch } },
    }));
  }

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchemaV2),
    defaultValues: DEFAULTS_V2,
    mode: 'onBlur',
  });

  const createMut = useCreateStrategyV2();
  const updateMut = useUpdateStrategyV2(strategyId);
  const strategyQ = useStrategy(strategyId);
  const sessionRole = useSessionStore((s) => s.role);
  const sessionIsSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const sessionRoles = useSessionStore((s) => s.user?.roles || []);
  const canEnableMaster = sessionIsSuperAdmin
    || ['operator', 'admin', 'both'].includes(sessionRole)
    || (Array.isArray(sessionRoles) && sessionRoles.some((role) => ['operator', 'admin', 'super_admin'].includes(role)));
  const isMasterEligibleSource = strategySource === 'EXTERNAL' || strategySource === 'MANUAL';
  const enableMasterMut = useEnableMasterStrategy();
  const catalogQ  = useStrategyConditionCatalog();
  const [isReservingToken, setIsReservingToken] = useState(false);
  const [tokenReservationError, setTokenReservationError] = useState('');
  const [tvGuideOpen, setTvGuideOpen] = useState(false);
  const [tokenReservationUnsupported, setTokenReservationUnsupported] = useState(false);
  // Once-per-mount guard for the automatic reservation effect below — survives
  // StrictMode's double effect invocation so exactly one request is fired.
  const autoTokenReservationAttemptedRef = useRef(false);
  const [markAsMasterOnCreate, setMarkAsMasterOnCreate] = useState(false);

  const tradeIn          = watch('execution.instrument') || 'OPTIONS';
  const productType      = watch('execution.product_type');
  const entryOrderType   = watch('execution.entry_order.order_type') || 'MARKET';
  const limitPriceMode   = watch('execution.entry_order.price_mode') || 'OFFSET_POINTS';
  const quantityMode     = watch('execution.quantity.mode') || 'FIXED_QTY';
  const allowPyramiding  = !!watch('execution.allow_pyramiding');
  const primaryTimeframe = watch('primary_timeframe') || '5m';
  const oppositeSignalHandling = watch('global_rules.opposite_signal_handling') || 'EXIT_AND_REVERSE';
  const busy = isSubmitting || createMut.isPending || updateMut.isPending;
  const editLifecycleBlocked = isEdit && ['STARTING', 'STOPPING'].includes(
    String(strategyQ.data?.latest_deployment_status || '').toUpperCase(),
  );

  // For preview panel — call watch() once per render (no arg = all fields)
  const allFormValues = watch();

  // Symbol Universe — derived values
  const selectedSymbol = symbolUniverse.symbols[0] || '';   // primary symbol (backward compat)
  const symbolCount    = symbolUniverse.symbols.length;
  const isMultiSymbol  = symbolCount > 1;

  // Derived execution state
  const isOptions     = tradeIn === 'OPTIONS';

  const productTypeOptions = useMemo(() => {
    if (isOptions) return [
      { value: 'INTRADAY', label: 'Intraday' },
      { value: 'MARGIN',   label: 'Margin' },
    ];
    if (strategySource === 'EXTERNAL' && ['FNO', 'COMMODITY', 'ETF'].includes(externalSegment))
      return [
        { value: 'INTRADAY', label: 'Intraday' },
        { value: 'MARGIN',   label: 'Margin' },
      ];
    return [
      { value: 'CNC',      label: 'CNC' },
      { value: 'INTRADAY', label: 'Intraday' },
      { value: 'MTF',      label: 'MTF' },
    ];
  }, [isOptions, strategySource, externalSegment]);

  // Manual product type options — same rules as External (instrument/segment aware)
  const manualProductTypeOptions = useMemo(() => {
    if (manualTradeIn === 'OPTION' || ['FNO', 'COMMODITY', 'ETF'].includes(manualSegment))
      return [
        { value: 'INTRADAY', label: 'Intraday' },
        { value: 'MARGIN',   label: 'Margin' },
      ];
    return [
      { value: 'CNC',      label: 'CNC' },
      { value: 'INTRADAY', label: 'Intraday' },
      { value: 'MTF',      label: 'MTF' },
    ];
  }, [manualTradeIn, manualSegment]);

  // Keep manual product type valid when its option set changes.
  useEffect(() => {
    if (!manualProductTypeOptions.some((o) => o.value === manualProductType)) {
      setManualProductType(manualProductTypeOptions[0]?.value || 'INTRADAY');
    }
  }, [manualProductTypeOptions, manualProductType]);

  // Manual: Index segment can execute only as Option (mirrors External index rule).
  useEffect(() => {
    if (manualSegment === 'INDEX' && manualTradeIn !== 'OPTION') {
      setManualTradeIn('OPTION');
    }
  }, [manualSegment, manualTradeIn]);

  const totalLegs     = (optionMapping.bullish?.length || 0) + (optionMapping.bearish?.length || 0);
  const isMultiLeg    = totalLegs > 1;
  const isIndexSelection = (
    (strategySource === 'EXTERNAL' && String(externalSegment || '').toUpperCase() === 'INDEX')
    ||
    String(symbolUniverse.segment || '').toUpperCase() === 'INDEX'
    || selectedSymbol.toUpperCase().includes('INDEX')
    || selectedSymbol.toUpperCase().includes('NIFTY')
    || selectedSymbol.toUpperCase().includes('SENSEX')
  );
  const showLimitPriceMode = entryOrderType === 'SL-L';
  const showLimitPriceValue = entryOrderType === 'SL-L';
  const legacyLimitModeOptions = limitPriceMode === 'ABSOLUTE'
    ? [{ value: 'ABSOLUTE', label: 'Legacy Absolute (Preserved)' }]
    : [];
  // Symbol is already an option contract — disable option config section
  const isSymbolOption = symbolUniverse.kind === 'OPTION';
  const effectiveWebhookToken = reservedStrategyToken || strategyQ.data?.strategy_token || strategyQ.data?.webhook_token || null;
  // TradingView integration (Phase 2): single visibility gate + shared URL.
  const webhookUrl = `${window.location.origin}/api/webhook/signal`;
  const isTvExternal = isTradingViewExternal(strategySource, externalSourceType);
  const showTvOptionsNote = showOptionsByAlertLotNote(tradeIn, quantityMode);

  // Keep form's `symbol` field in sync with universe primary (for schema validation + payload compat)
  useEffect(() => {
    setValue('symbol', symbolUniverse.symbols[0] || '', { shouldDirty: false, shouldValidate: false });
  }, [symbolUniverse.symbols, setValue]);

  // Auto-correct execution instrument + risk units when switching to multi-symbol
  useEffect(() => {
    if (!isMultiSymbol) return;
    if (getValues('execution.instrument') === 'OPTIONS') {
      setValue('execution.instrument', 'UNDERLYING', { shouldDirty: true });
      toast.warn('Options execution is available only for single-symbol strategies. Switched to Underlying.');
    }
    for (const key of ['target', 'stop_loss', 'trailing_stop']) {
      if (getValues(`risk.${key}.type`) === 'points') {
        setValue(`risk.${key}.type`, 'percent', { shouldDirty: true });
      }
    }
  }, [isMultiSymbol]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-correct execution instrument when the selected symbol is itself an option contract
  useEffect(() => {
    if (!isSymbolOption) return;
    if (getValues('execution.instrument') === 'OPTIONS') {
      setValue('execution.instrument', 'UNDERLYING', { shouldDirty: true });
      toast.warn('Options execution is not available when the selected symbol is already an option contract. Switched to Underlying.');
    }
  }, [isSymbolOption]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isIndexSelection) return;
    if (getValues('execution.instrument') !== 'UNDERLYING') return;
    setValue('execution.instrument', 'OPTIONS', { shouldDirty: true, shouldValidate: true });
    toast.warn('Index strategies can execute only in Options mode. Switched to Options.');
  }, [getValues, isIndexSelection, setValue]);

  // Reset Trade In Next Exp to OFF when leaving OPTIONS so re-entering OPTIONS never shows a stale value.
  // shouldDirty:false — normalizing this option-only field must not mark an Underlying strategy as modified.
  useEffect(() => {
    if (tradeIn !== 'OPTIONS' && getValues('execution.trade_in_next_exp') !== 'OFF') {
      setValue('execution.trade_in_next_exp', 'OFF', { shouldDirty: false, shouldValidate: false });
    }
  }, [tradeIn, getValues, setValue]);

  // Pyramiding UI is not usable for OPTIONS; force the form value to false when Trade In is Options.
  useEffect(() => {
    if (isPyramidingToggleEnabled({ tradeIn })) return;
    if (!getValues('execution.allow_pyramiding')) return;
    setValue('execution.allow_pyramiding', false, { shouldDirty: true, shouldValidate: false });
  }, [tradeIn, getValues, setValue]);

  // Auto-reset product type when current selection is no longer valid
  useEffect(() => {
    const valid = productTypeOptions.map(o => o.value);
    if (productType && !valid.includes(productType)) {
      setValue('execution.product_type', 'INTRADAY', { shouldDirty: true });
    }
  }, [productType, productTypeOptions, setValue]);

  // Auto-reserve one Strategy Token for a NEW External strategy (provider-
  // independent: TradingView, AmiBroker, Python). Guided creation mounts with
  // EXTERNAL preselected, so this effect is the firing path there; the admin
  // for_user path additionally reserves via handleStrategySourceChange.
  //
  // isReservingToken is read as a guard but intentionally NOT listed as a
  // dependency: listing it made this effect re-run on its own state update,
  // and the previous cleanup then cancelled the in-flight request — the
  // Phase 1 regression that left guided creation stuck at "Reserving token…".
  // The ref makes the attempt exactly-once per mount, including under
  // StrictMode double invocation. The promise handlers therefore apply
  // unconditionally (same pattern as requestReservedStrategyToken /
  // handleStrategySourceChange); a late response after unmount is a no-op.
  useEffect(() => {
    if (!shouldAttemptAutoReservation({
      isEdit,
      strategySource,
      reservedStrategyToken,
      isReservingToken,
      tokenReservationUnsupported,
    })) return;
    if (autoTokenReservationAttemptedRef.current) return;
    autoTokenReservationAttemptedRef.current = true;
    setIsReservingToken(true);
    setTokenReservationError('');
    strategiesApi.reserveToken({})
      .then((resp) => {
        setReservedStrategyToken(extractReservedToken(resp));
      })
      .catch((err) => {
        if (isTokenReservationUnsupportedError(err)) {
          setTokenReservationUnsupported(true);
          setTokenReservationError('');
        } else {
          setTokenReservationError(err?.message || 'Failed to reserve strategy token');
          toast.error(err?.message || 'Failed to reserve strategy token');
        }
      })
      .finally(() => setIsReservingToken(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, strategySource, reservedStrategyToken, tokenReservationUnsupported]);

  // Partial-exit policy is runtime-effective only with BY_ALERT mode and is
  // mutually exclusive with pyramiding. Keep UI and saved config aligned.
  useEffect(() => {
    if (quantityMode === 'BY_ALERT') return;
    if (!getValues('execution.allow_partial_exit')) return;
    setValue('execution.allow_partial_exit', false, { shouldDirty: true, shouldValidate: false });
  }, [quantityMode, getValues, setValue]);

  // When pyramiding is enabled, partial-exit must be off (non-pyramiding only).
  useEffect(() => {
    if (!allowPyramiding) return;
    if (!getValues('execution.allow_partial_exit')) return;
    setValue('execution.allow_partial_exit', false, { shouldDirty: true, shouldValidate: false });
  }, [allowPyramiding, getValues, setValue]);

  function requestReservedStrategyToken() {
    if (isEdit) return;
    if (strategySource !== 'EXTERNAL') return;
    if (isReservingToken) return;
    if (tokenReservationUnsupported) return;
    setIsReservingToken(true);
    setTokenReservationError('');
    strategiesApi.reserveToken({})
      .then((resp) => setReservedStrategyToken(extractReservedToken(resp)))
      .catch((err) => {
        if (isTokenReservationUnsupportedError(err)) {
          setTokenReservationUnsupported(true);
          setTokenReservationError('');
        } else {
          setTokenReservationError(err?.message || 'Failed to reserve strategy token');
          toast.error(err?.message || 'Failed to reserve strategy token');
        }
      })
      .finally(() => setIsReservingToken(false));
  }

  function handleStrategySourceChange(nextValue) {
    setStrategySource(nextValue);
    if (!isEdit && nextValue === 'INTERNAL') {
      setTokenReservationError('');
    }
    if (!isEdit && nextValue === 'EXTERNAL' && !reservedStrategyToken && !isReservingToken && !tokenReservationUnsupported) {
      setIsReservingToken(true);
      setTokenReservationError('');
      strategiesApi.reserveToken({})
        .then((resp) => setReservedStrategyToken(extractReservedToken(resp)))
        .catch((err) => {
          if (isTokenReservationUnsupportedError(err)) {
            setTokenReservationUnsupported(true);
            setTokenReservationError('');
          } else {
            setTokenReservationError(err?.message || 'Failed to reserve strategy token');
            toast.error(err?.message || 'Failed to reserve strategy token');
          }
        })
        .finally(() => setIsReservingToken(false));
    }
  }

  // ── Symbol Universe handlers ───────────────────────────────────────────────

  function setUniverseMode(mode) {
    setSymbolUniverse((prev) => {
      if (prev.mode === mode) return prev;
      if (mode === 'PRESET_WATCHLIST') return { mode, symbols: [], segment: null, kind: null, preset_key: null };
      if (mode === 'CUSTOM_WATCHLIST') return { mode, symbols: [], segment: null, kind: null, preset_key: null };
      // SINGLE_SYMBOL — keep first symbol; kind will be re-derived on next picker selection
      return { mode, symbols: prev.symbols.slice(0, 1), segment: prev.segment, kind: prev.kind, preset_key: null };
    });
  }

  function handlePresetSelect(presetKey) {
    const preset = PRESET_WATCHLISTS.find((p) => p.key === presetKey);
    if (!preset) return;
    setSymbolUniverse({ mode: 'PRESET_WATCHLIST', symbols: preset.symbols, segment: preset.segment, preset_key: preset.key });
  }

  function handleRemoveUniverseSymbol(sym) {
    setSymbolUniverse((prev) => {
      const next = prev.symbols.filter((s) => s !== sym);
      return { ...prev, symbols: next, segment: next.length === 0 ? null : prev.segment };
    });
  }

  // Edit mode: fetch strategy and hydrate form
  useEffect(() => {
    if (!isEdit || !strategyQ.data || hydrated) return;

    const raw = strategyQ.data;
    const cj = (raw.config_json?.schema_version === '2.0')
      ? raw.config_json
      : (raw.config?.schema_version === '2.0')
      ? raw.config
      : null;
    if (!cj) {
      toast.error('This strategy uses an older format that cannot be edited here.');
      navigate(ROUTES.STRATEGIES);
      return;
    }

    const { formValues, sectionTrees: trees, sectionEntryConfigs: entryConfigs, optionMapping: mapping, selectedLotSize: lotSize, symbolUniverse: su } =
      hydrateFromV2Payload(cj);

    reset(formValues);
    setSectionTrees(trees);
    if (entryConfigs) setSectionEntryConfig((prev) => ({ ...prev, ...entryConfigs }));
    setOptionMapping(mapping);
    setSelectedLotSize(lotSize);
    setSymbolUniverse(su);
    // Phase 2 hook point: read strategy_source from saved config when backend migration 013 is applied.
    // All current saved strategies are INTERNAL; default safely.
    const savedSource = cj.strategy_source || 'INTERNAL';
    setStrategySource(savedSource);
    if (savedSource === 'EXTERNAL') {
      if (cj.external_source_type) {
        setExternalSourceType(cj.external_source_type === 'PYTHON_AMIBROKER' ? 'PYTHON' : cj.external_source_type);
      }
      if (cj.external_exchange) setExternalExchange(cj.external_exchange);
      if (cj.external_segment) setExternalSegment(cj.external_segment);
      if (cj.external_broker_name) setExternalBrokerName(cj.external_broker_name);
      const savedMode = (cj.strategy_meta?.external_mode || 'SIMPLE').toUpperCase();
      setExternalMode(savedMode === 'ADVANCED' ? 'ADVANCED' : 'SIMPLE');
    } else if (savedSource === 'MANUAL') {
      const mt = cj.manual_trading || {};
      if (mt.exchange) setManualExchange(mt.exchange);
      if (mt.segment) setManualSegment(mt.segment);
      if (mt.trade_in) setManualTradeIn(mt.trade_in);
      if (mt.product_type) setManualProductType(mt.product_type);
      if (mt.qty_mode) setManualQtyMode(mt.qty_mode);
      setManualPyramiding(!!mt.pyramiding);
      setManualTradeInNextExp(normalizeTradeInNextExp(cj.execution?.trade_in_next_exp));
    }
    setHydrated(true);
  }, [isEdit, strategyQ.data, hydrated, navigate, reset]);

  // Sync lot size across all option legs when selectedLotSize changes
  useEffect(() => {
    setOptionMapping((prev) => {
      let changed = false;
      const next = {};
      for (const signal of ['bullish', 'bearish']) {
        const legs = prev[signal] || [];
        next[signal] = legs.map((leg) => {
          const lotSize = Math.max(1, Number(selectedLotSize) || 1);
          if (Math.max(1, Number(leg.lotSize) || 1) === lotSize) return leg;
          changed = true;
          return { ...leg, lotSize };
        });
      }
      return changed ? next : prev;
    });
  }, [selectedLotSize]);

  // ── Section enable / tree init ─────────────────────────────────────────────

  function handleSectionEnable(key, enabled) {
    setValue(`sections.${key}.enabled`, enabled, { shouldDirty: true });
    if (enabled && !sectionTrees[key]) {
      setSectionTrees((prev) => ({ ...prev, [key]: emptyGroup() }));
    }
  }

  // ── Option leg CRUD ────────────────────────────────────────────────────────

  function updateLeg(signal, legId, patch) {
    setOptionMapping((prev) => ({
      ...prev,
      [signal]: (prev[signal] || []).map((leg) => {
        if (leg.id !== legId) return leg;
        const nextLeg = { ...leg, ...patch };
        if (
          nextLeg.strikeSelectionMode === 'manual' &&
          (nextLeg.manualStrike === 'OTM' || nextLeg.manualStrike === 'ITM')
        ) {
          nextLeg.offset = Math.max(1, Number(nextLeg.offset) || 1);
        }
        return nextLeg;
      }),
    }));
  }

  function addLeg(signal) {
    const defaultType = signal === 'bullish' ? 'CE' : 'PE';
    // Auto-correct Option Premium → Underlying when adding a second leg (multi-leg disables Option Premium)
    const currentTotal = (optionMapping.bullish?.length || 0) + (optionMapping.bearish?.length || 0);
    if (currentTotal >= 1) {
      let warned = false;
      for (const riskKey of ['target', 'stop_loss', 'trailing_stop']) {
        if (watch(`risk.${riskKey}.apply_on`) === 'OPTION_PREMIUM') {
          setValue(`risk.${riskKey}.apply_on`, 'UNDERLYING', { shouldDirty: true });
          if (!warned) {
            toast.error('Option Premium risk is available only for single-leg, single-symbol strategies. Switched to Underlying.');
            warned = true;
          }
        }
      }
    }
    // (Multi-symbol auto-correction is handled separately when the symbol picker adds a second symbol)
    setOptionMapping((prev) => ({
      ...prev,
      [signal]: [...(prev[signal] || []), createDefaultLeg(defaultType, selectedSymbol, selectedLotSize)],
    }));
  }

  function duplicateLeg(signal, legId) {
    setOptionMapping((prev) => {
      const legs = prev[signal] || [];
      const idx  = legs.findIndex((leg) => leg.id === legId);
      if (idx < 0) return prev;
      const cloned   = { ...legs[idx], id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}` };
      const nextLegs = [...legs.slice(0, idx + 1), cloned, ...legs.slice(idx + 1)];
      return { ...prev, [signal]: nextLegs };
    });
  }

  function removeLeg(signal, legId) {
    setOptionMapping((prev) => ({
      ...prev,
      [signal]: (prev[signal] || []).filter((leg) => leg.id !== legId),
    }));
  }

  // Shared option-mapping legs editor — reused by the Execution section
  // (INTERNAL/EXTERNAL) and the Manual Strategy "Option Mapping" section so the
  // UI + payload shape (bullish_signal_legs / bearish_signal_legs) stay identical.
  // hideQuantity: Manual hides Lots / Lot size / Qty from the UI (qty is entered
  // per symbol in the Manual Trading watchlist); the payload shape is unchanged.
  function renderOptionLegsEditor({ hideQuantity = false } = {}) {
    return (
              <div className="space-y-4 mt-3">
                {[
                  { key: 'bullish', title: 'Bullish Signal', borderColor: 'border-bull/30', bgColor: 'bg-bull/5', dotColor: 'bg-bull', labelColor: 'text-bull' },
                  { key: 'bearish', title: 'Bearish Signal', borderColor: 'border-bear/30', bgColor: 'bg-bear/5', dotColor: 'bg-bear', labelColor: 'text-bear' },
                ].map((block) => {
                  const signalKey = block.key;
                  const legs = optionMapping[signalKey] || [];
                  return (
                    <div key={signalKey} className={cn('rounded-xl border p-4 space-y-3', block.borderColor, block.bgColor)}>
                      {/* Signal block header */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={cn('w-2 h-2 rounded-full shrink-0', block.dotColor)} />
                          <span className={cn('text-xs font-semibold', block.labelColor)}>{block.title}</span>
                          <span className="text-2xs font-mono text-txt-muted">
                            {legs.length} leg{legs.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-secondary text-xs py-1 px-3"
                          onClick={() => addLeg(signalKey)}
                        >
                          + Add Leg
                        </button>
                      </div>

                      {/* Legs */}
                      <div className="space-y-3">
                        {legs.map((leg, idx) => {
                          const normalizedLots    = Math.max(1, Number(leg.lots)    || 1);
                          const normalizedLotSize = Math.max(1, Number(leg.lotSize) || 1);
                          const quantity          = normalizedLots * normalizedLotSize;
                          const showOffset        = leg.strikeSelectionMode === 'manual' && (leg.manualStrike === 'OTM' || leg.manualStrike === 'ITM');
                          const isBuy             = leg.action === 'BUY';
                          return (
                            <div key={leg.id} className="rounded-xl border border-bg-border bg-bg-card p-2.5 space-y-2">

                              {/* Header: badge · Leg N  |  Lot size · Qty · Duplicate · Remove */}
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={cn(
                                    'text-2xs font-mono font-bold px-2 py-0.5 rounded border',
                                    isBuy
                                      ? 'text-bull border-bull/30 bg-bull/10'
                                      : 'text-bear border-bear/30 bg-bear/10'
                                  )}>
                                    {leg.action}
                                  </span>
                                  <span className="text-xs text-txt-secondary font-mono">Leg {idx + 1}</span>
                                </div>
                                <div className="flex items-center gap-2.5 text-2xs font-mono flex-wrap justify-end">
                                  {!hideQuantity && (
                                    <>
                                      <span className="text-txt-muted">
                                        Lot size <span className="text-txt-secondary ml-0.5">{normalizedLotSize}</span>
                                      </span>
                                      <span className="w-px h-3 bg-bg-border/60 shrink-0" />
                                      <span className="text-txt-muted">
                                        Qty <span className="text-amber font-semibold ml-0.5">{quantity}</span>
                                      </span>
                                      <span className="w-px h-3 bg-bg-border/60 shrink-0" />
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    className="text-txt-muted hover:text-txt-primary transition-colors"
                                    onClick={() => duplicateLeg(signalKey, leg.id)}
                                  >
                                    Duplicate
                                  </button>
                                  <button
                                    type="button"
                                    className="text-bear/60 hover:text-bear transition-colors"
                                    onClick={() => removeLeg(signalKey, leg.id)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>

                              {/* Labels row + aligned fields row — each column: label above, control below */}
                              <div className="flex flex-wrap gap-x-3 gap-y-2">

                                <div>
                                  <div className="field-label mb-1">Action</div>
                                  <FSelect
                                    className="w-20"
                                    value={leg.action}
                                    options={[{ value: 'BUY', label: 'Buy' }, { value: 'SELL', label: 'Sell' }]}
                                    onChange={(e) => updateLeg(signalKey, leg.id, { action: e.target.value })}
                                  />
                                </div>

                                <div>
                                  <div className="field-label mb-1">Type</div>
                                  <FSelect
                                    className="w-16"
                                    value={leg.optionType}
                                    options={['CE', 'PE']}
                                    onChange={(e) => updateLeg(signalKey, leg.id, { optionType: e.target.value })}
                                  />
                                </div>

                                <div>
                                  <div className="field-label mb-1">Expiry</div>
                                  <FSelect
                                    className="w-36"
                                    value={leg.expiry}
                                    options={getExpiryOptionsForSymbol(selectedSymbol, symbolUniverse.kind, strategySource)}
                                    onChange={(e) => updateLeg(signalKey, leg.id, { expiry: e.target.value })}
                                  />
                                </div>

                                <div>
                                  <div className="field-label mb-1">Mode</div>
                                  <FSelect
                                    className="w-24"
                                    value={leg.strikeSelectionMode}
                                    options={[
                                      { value: 'manual',          label: 'Manual'  },
                                      { value: 'closest_premium', label: 'Premium' },
                                    ]}
                                    onChange={(e) => updateLeg(signalKey, leg.id, { strikeSelectionMode: e.target.value })}
                                  />
                                </div>

                                {leg.strikeSelectionMode === 'manual' ? (
                                  <div>
                                    <div className="field-label mb-1">Strike</div>
                                    <FSelect
                                      className="w-20"
                                      value={leg.manualStrike}
                                      options={[
                                        { value: 'ATM', label: 'ATM' },
                                        { value: 'OTM', label: 'OTM' },
                                        { value: 'ITM', label: 'ITM' },
                                      ]}
                                      onChange={(e) => updateLeg(signalKey, leg.id, { manualStrike: e.target.value })}
                                    />
                                  </div>
                                ) : (
                                  <div>
                                    <div className="field-label mb-1">Premium</div>
                                    <FInput
                                      className="w-20"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={leg.premium}
                                      onChange={(e) => updateLeg(signalKey, leg.id, { premium: e.target.value })}
                                    />
                                  </div>
                                )}

                                {showOffset && (
                                  <div>
                                    <div className="field-label mb-1">Offset</div>
                                    <FInput
                                      className="w-14"
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={leg.offset}
                                      onChange={(e) => updateLeg(signalKey, leg.id, { offset: Math.max(1, Number(e.target.value) || 1) })}
                                    />
                                  </div>
                                )}

                                {!hideQuantity && (
                                  <div>
                                    <div className="field-label mb-1">Lots</div>
                                    <FInput
                                      className="w-14"
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={leg.lots}
                                      onChange={(e) => updateLeg(signalKey, leg.id, { lots: Math.max(1, Number(e.target.value) || 1) })}
                                    />
                                  </div>
                                )}

                              </div>

                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 px-1 text-2xs font-mono text-txt-muted border-t border-bg-border/40 pt-2">
                  <span className="text-txt-secondary">Execution order:</span>
                  BUY legs first on entry · SELL legs first on exit
                </div>
              </div>
    );
  }

  function handleSymbolPick(row) {
    if (!row?.symbol) {
      toast.error('Selected symbol is invalid');
      return;
    }

    // CUSTOM_WATCHLIST mode — add to list with validation
    if (symbolUniverse.mode === 'CUSTOM_WATCHLIST') {
      if (symbolUniverse.symbols.includes(row.symbol)) {
        toast.error('This symbol is already in the watchlist.');
        return;
      }
      if (symbolUniverse.segment && symbolUniverse.segment !== row.segment) {
        toast.error('Custom watchlists can include symbols from one segment only.');
        return;
      }
      setSymbolUniverse((prev) => ({
        ...prev,
        symbols: [...prev.symbols, row.symbol],
        segment: prev.segment || row.segment || null,
      }));
      setSymbolPickerOpen(false);
      return;
    }

    // SINGLE_SYMBOL mode — replace universe + sync lot size / expiry
    const lotSize = Math.max(1, Number(row.lot_size) || 1);
    const kind    = classifySymbolKind(row.symbol, row.segment, row.instrument_type);
    const defaultExpiry = defaultUiExpiryForSymbol(row.symbol, row.underlying_symbol || '', kind);
    setSelectedLotSize(lotSize);
    setSymbolUniverse({ mode: 'SINGLE_SYMBOL', symbols: [row.symbol], segment: row.segment || null, kind, preset_key: null });
    setOptionMapping((prev) => ({
      bullish: (prev.bullish || []).map((leg) => ({ ...leg, lotSize, expiry: defaultExpiry })),
      bearish: (prev.bearish || []).map((leg) => ({ ...leg, lotSize, expiry: defaultExpiry })),
    }));
    setSymbolPickerOpen(false);
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  async function onSubmit(data) {
    if (editLifecycleBlocked) {
      toast.error('Close all open positions and wait for pending orders to finish before editing this strategy.');
      return;
    }
    // Condition tree validation applies only to internal strategies
    if (strategySource === 'INTERNAL') {
      const sectionLabels = {
        long_entry: 'Long Entry', long_exit: 'Long Exit',
        short_entry: 'Short Entry', short_exit: 'Short Exit',
      };
      const treeErrors = [];
      for (const key of ['long_entry', 'long_exit', 'short_entry', 'short_exit']) {
        const isEnabled   = data.sections[key]?.enabled;
        const tree        = sectionTrees[key];
        const hasChildren = tree && (tree.children || []).length > 0;
        if (isEnabled && !hasChildren) {
          treeErrors.push(`${sectionLabels[key]} is enabled but has no conditions.`);
        }
      }
      if (treeErrors.length > 0) {
        treeErrors.forEach((msg) => toast.error(msg));
        return;
      }
    }

    if (strategySource === 'INTERNAL' && symbolUniverse.symbols.length === 0) {
      toast.error('Select at least one symbol for the strategy.');
      return;
    }

    if (strategySource === 'EXTERNAL') {
      if (!externalExchange) {
        toast.error('Select exchange for the external strategy.');
        return;
      }
      if (!externalSegment) {
        toast.error('Select segment for the external strategy.');
        return;
      }
      if (externalSourceType === 'PYTHON' && !externalBrokerName) {
        toast.error('Select broker for Python-based external strategies.');
        return;
      }
    }

    if (strategySource === 'INTERNAL' && data.execution?.quantity?.mode === 'BY_ALERT') {
      toast.error('BY_ALERT quantity mode is allowed only for External strategies.');
      return;
    }

    if (strategySource === 'MANUAL') {
      if (!manualExchange) { toast.error('Select exchange for the manual strategy.'); return; }
      if (!manualSegment)  { toast.error('Select segment for the manual strategy.'); return; }
    }

    try {
      const payload = strategySource === 'MANUAL'
        ? buildManualStrategyPayload(data, {
            exchange: manualExchange,
            segment: manualSegment,
            tradeIn: manualTradeIn,
            productType: manualProductType,
            qtyMode: manualQtyMode,
            pyramiding: manualPyramiding,
            tradeInNextExp: manualTradeInNextExp,
            optionMapping,
            lotSize: selectedLotSize,
          })
        : buildSchemaV2Payload(
        data,
        sectionTrees,
        optionMapping,
        selectedLotSize,
        symbolUniverse,
        sectionEntryConfig,
        strategySource,
        strategySource === 'EXTERNAL' ? externalSourceType : null,
        !isEdit && strategySource === 'EXTERNAL' ? reservedStrategyToken : '',
        strategySource === 'EXTERNAL' ? externalExchange : null,
        strategySource === 'EXTERNAL' ? externalSegment : null,
        strategySource === 'EXTERNAL' ? externalBrokerName : null,
        strategySource === 'EXTERNAL' ? externalMode : 'SIMPLE',
      );
      if (import.meta.env.DEV) {
        console.debug('[strategy-save] global_rules payload:', payload.global_rules);
        console.debug('[strategy-save] final request body:', isEdit
          ? { ...payload, user_id: undefined }
          : { ...payload, user_id: undefined }
        );
      }
      // When creating on behalf of another user, include user_id
      if (forUserId && !isEdit) {
        payload.user_id = parseInt(forUserId, 10);
      }
      if (isEdit) {
        await updateMut.mutateAsync(payload);
        toast.success(`Strategy updated: ${payload.strategy_meta?.name || 'OK'}`);
      } else {
        const created = await createMut.mutateAsync(payload);
        if (created?.strategy_token || created?.webhook_token) {
          setReservedStrategyToken(created?.strategy_token || created?.webhook_token);
        }
        toast.success(`Strategy created: ${created?.strategy_meta?.name || created?.name || 'OK'}`);
        if (markAsMasterOnCreate && canEnableMaster && isMasterEligibleSource && created?.id) {
          try {
            await enableMasterMut.mutateAsync(created.id);
            toast.success(strategySource === 'MANUAL'
              ? 'Manual Master mode enabled. Activate it from Strategies when you are ready.'
              : 'Enabled as Master');
          } catch (e) {
            toast.error(e?.message || 'Saved, but failed to enable as Master');
          }
        }
      }
      navigate(ROUTES.STRATEGIES);
    } catch (err) {
      toast.error(err?.message || 'Failed to save strategy');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // Direct /strategies/new access without valid guided params (and not admin
  // for_user creation) fails safe to the guided selection page. This early
  // return is placed after every hook call so hook ordering stays stable.
  if (builderEntry.mode === 'redirect') {
    return <Navigate to={ROUTES.STRATEGIES_CREATE} replace />;
  }

  // Guided creation returns to the guided selection page on Back/Cancel so an
  // External user can pick another provider; all other entries keep the
  // existing Strategies-list target.
  const backTarget = isGuidedCreate ? ROUTES.STRATEGIES_CREATE : ROUTES.STRATEGIES;

  if (isEdit && strategyQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3 text-txt-muted text-sm font-mono">
        <Spinner size={20} /> Loading strategy…
      </div>
    );
  }

  if (isEdit && strategyQ.isError) {
    return (
      <div className="panel p-8 text-center space-y-3">
        <p className="text-bear text-sm font-mono">Failed to load strategy for editing.</p>
        <p className="text-txt-muted text-xs font-mono">{strategyQ.error?.message}</p>
        <button className="btn-ghost text-xs mt-2" onClick={() => navigate(ROUTES.STRATEGIES)}>
          ← Back to Strategies
        </button>
      </div>
    );
  }

  return (
    <>
      <SymbolPickerModal
        open={symbolPickerOpen}
        selectedSymbol={selectedSymbol}
        onClose={() => setSymbolPickerOpen(false)}
        onSelect={handleSymbolPick}
      />
      <NotesModal
        open={notesOpen}
        value={watch('description') || ''}
        onClose={() => setNotesOpen(false)}
        onSave={(text) => setValue('description', text, { shouldDirty: true })}
      />

      {/* Banner when creating for another user */}
      {forUserId && !isEdit && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-amber/30 bg-amber/10 text-amber text-xs font-mono flex items-center gap-2">
          <span>Creating strategy for user #{forUserId}</span>
        </div>
      )}

      {/* ── Sticky toolbar ── */}
      {editLifecycleBlocked && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-amber/30 bg-amber/10 text-amber text-xs font-mono">
          Close all open positions and wait for pending orders to finish before editing this strategy.
        </div>
      )}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 py-3 border-b border-bg-border bg-bg-base/95 backdrop-blur-sm mb-6 -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(backTarget)}
            className="btn-ghost text-xs flex items-center gap-1.5"
          >
            <ArrowLeft size={12} /> {isGuidedCreate ? 'Back' : 'Strategies'}
          </button>
          <BuilderModeToggle />
        </div>
        <div className="hidden lg:flex items-center gap-2">
          <span className="text-2xs text-txt-muted font-mono hidden sm:block">
            {isEdit ? 'Editing strategy' : 'New strategy'}
          </span>
          <button
            type="button"
            onClick={() => navigate(backTarget)}
            className="btn-ghost text-xs"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="strategy-form"
            disabled={busy || editLifecycleBlocked}
            className="btn-primary text-xs flex items-center gap-1.5"
          >
            {busy ? <Spinner size={12} className="text-bg-base" /> : <Zap size={11} />}
            {busy ? 'Saving…' : isEdit ? 'Update Strategy' : 'Save Strategy'}
          </button>
        </div>
      </div>

      {/* ── Mobile preview accordion ── */}
      <div className="lg:hidden mb-5">
        <details className="card">
          <summary className="px-3 py-2.5 cursor-pointer text-xs font-medium text-txt-label select-none list-none flex items-center justify-between">
            <span>Strategy Preview</span>
            <ChevronDown size={12} className="text-txt-muted" />
          </summary>
          <div className="px-3 pb-3 pt-1 border-t border-bg-border/50">
            <StrategyPreviewPanel
              formValues={allFormValues}
              sectionTrees={sectionTrees}
              optionMapping={optionMapping}
              strategySource={strategySource}
              externalSourceType={externalSourceType}
              manual={{ exchange: manualExchange, segment: manualSegment, tradeIn: manualTradeIn, qtyMode: manualQtyMode }}
            />
          </div>
        </details>
      </div>

      {/* ── Two-column layout ── */}
      <div className="flex gap-6 items-start">

        {/* ── Left: form ── */}
        <form id="strategy-form" onSubmit={handleSubmit(onSubmit)} className="flex-1 min-w-0 space-y-3">

          {/* ── Strategy Source ── */}
          {/* Guided creation: the type was chosen on /strategies/create and is
              fixed for this session — show a read-only chip instead of the
              selector (use Back to pick a different type/provider). */}
          {isGuidedCreate ? (
            <div className="flex items-center gap-2 bg-bg-surface border border-amber/25 rounded-lg px-3 py-1.5 w-fit">
              <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Strategy Type</span>
              <span className="text-xs font-medium text-amber">
                {strategySource === 'INTERNAL' && 'Internal Strategy'}
                {strategySource === 'EXTERNAL' && 'External Strategy'}
                {strategySource === 'MANUAL' && 'Manual Strategy'}
              </span>
            </div>
          ) : (
          <div className="flex items-center gap-0.5 bg-bg-surface border border-bg-border rounded-lg p-0.5 w-fit">
            {[
              { value: 'INTERNAL', label: 'Internal Strategy' },
              { value: 'EXTERNAL', label: 'External Strategy' },
              { value: 'MANUAL',   label: 'Manual Strategy' },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleStrategySourceChange(value)}
                className={cn(
                  'px-4 py-1.5 rounded text-xs font-medium transition-colors',
                  strategySource === value
                    ? 'bg-amber/10 text-amber border border-amber/25'
                    : 'text-txt-muted hover:text-txt-secondary'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          )}

          {/* ── 1. Strategy Basics ── */}
          <Section title="Strategy Basics" hint="Core identity" accent="bg-amber/70">
            <div className="flex items-start gap-3">
              <div className={strategySource === 'EXTERNAL' ? 'flex-1 min-w-0' : 'flex-1'}>
                <Field label="Name" error={errors?.name?.message} required>
                  <FInput {...register('name')} placeholder="e.g. NIFTY Momentum v2" />
                </Field>
              </div>
              {strategySource === 'EXTERNAL' && (
                <div className="shrink-0 pt-[22px]">
                  <div className="flex rounded border border-bg-border overflow-hidden">
                    {['SIMPLE', 'ADVANCED'].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        disabled={mode === 'ADVANCED'}
                        onClick={() => mode !== 'ADVANCED' && setExternalMode(mode)}
                        className={cn(
                          'px-3 py-1.5 text-2xs font-mono font-medium transition-colors',
                          externalMode === mode
                            ? 'bg-amber/10 text-amber border-r border-bg-border'
                            : mode === 'ADVANCED'
                              ? 'bg-bg-surface/30 text-txt-muted/40 cursor-not-allowed border-r border-bg-border'
                              : 'bg-bg-surface/50 text-txt-muted hover:text-txt-secondary border-r border-bg-border',
                          mode === 'ADVANCED' && 'border-r-0',
                        )}
                      >
                        {mode === 'SIMPLE' ? 'Simple' : 'Advanced'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {canEnableMaster && isMasterEligibleSource && !isEdit && (
                <div className="shrink-0 pt-[22px]">
                  <label
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded border border-bg-border text-txt-muted hover:text-amber hover:border-amber/40 text-2xs font-mono font-medium cursor-pointer transition-colors"
                    title="After save, this strategy will be enabled as Master"
                  >
                    <input
                      type="checkbox"
                      className="accent-amber"
                      checked={markAsMasterOnCreate}
                      onChange={(e) => setMarkAsMasterOnCreate(e.target.checked)}
                    />
                    Enable as Master
                  </label>
                </div>
              )}
              {canEnableMaster && isMasterEligibleSource && isEdit && (() => {
                const masterMode = strategySource === 'MANUAL' ? 'MASTER_MANUAL' : 'MASTER_EXTERNAL';
                const isMaster = strategyQ.data?.strategy_mode === masterMode;
                if (isMaster) {
                  return (
                    <div className="shrink-0 pt-[22px]">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-amber/40 bg-amber/10 text-amber text-2xs font-mono font-medium"
                        title="This strategy fans signals out to mapped users"
                      >
                        <CheckCircle2 size={11} /> Master
                      </span>
                    </div>
                  );
                }
                return (
                  <div className="shrink-0 pt-[22px]">
                    <button
                      type="button"
                      disabled={enableMasterMut.isPending}
                      onClick={async () => {
                        if (!window.confirm('Enable this strategy as a Master? This will allow mapped users to receive its signals. This action cannot be undone.')) return;
                        try {
                          await enableMasterMut.mutateAsync(strategyId);
                          toast.success('Enabled as Master');
                        } catch (e) {
                          toast.error(e?.message || 'Failed to enable as master');
                        }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-bg-border text-txt-muted hover:text-amber hover:border-amber/40 text-2xs font-mono font-medium transition-colors disabled:opacity-50"
                      title="Mark this strategy as a master; mapped users will receive its signals"
                    >
                      {enableMasterMut.isPending ? <Spinner size={9} /> : <Plus size={11} />}
                      Enable as Master
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Notes button — opens modal */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setNotesOpen(true)}
                className="text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors flex items-center gap-1.5"
              >
                <Plus size={10} />
                {watch('description') ? 'Edit notes' : 'Add notes'}
              </button>
              {watch('description') && (
                <span className="text-2xs font-mono px-1.5 py-0.5 rounded bg-bg-surface border border-bg-border text-txt-secondary">
                  Note saved
                </span>
              )}
              {/* hidden field keeps description in form state */}
              <input type="hidden" {...register('description')} />
            </div>
          </Section>

          {/* ── 2. Symbol Universe ── */}
          {strategySource === 'INTERNAL' && (
          <Section title="Symbol Universe" hint="Which symbols this strategy monitors" accent="bg-warn/50">
            {/* Always-registered hidden field keeps symbol in form state across all modes */}
            <input type="hidden" {...register('symbol')} />

            {/* Mode selector */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { value: 'SINGLE_SYMBOL',    label: 'Single Symbol'   },
                { value: 'PRESET_WATCHLIST', label: 'Preset Watchlist' },
                { value: 'CUSTOM_WATCHLIST', label: 'Custom Watchlist' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setUniverseMode(value)}
                  className={cn(
                    'px-3 py-1 rounded-lg text-xs font-medium border transition-colors',
                    symbolUniverse.mode === value
                      ? 'border-amber/40 bg-amber/10 text-amber'
                      : 'border-bg-border text-txt-secondary hover:border-amber/25 hover:text-txt-primary'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Single Symbol */}
            {symbolUniverse.mode === 'SINGLE_SYMBOL' && (
              <Field label="Symbol" error={errors?.symbol?.message} required>
                <button
                  type="button"
                  className="input-base w-full text-left flex items-center justify-between"
                  onClick={() => setSymbolPickerOpen(true)}
                >
                  <span className={cn('font-mono truncate', selectedSymbol ? 'text-txt-primary' : 'text-txt-muted')}>
                    {selectedSymbol || 'Search symbol…'}
                  </span>
                  <ChevronDown size={12} className="text-txt-muted shrink-0" />
                </button>
              </Field>
            )}

            {/* Preset Watchlist */}
            {symbolUniverse.mode === 'PRESET_WATCHLIST' && (
              <div className="space-y-2">
                <Field label="Watchlist">
                  <FSelect
                    options={[
                      { value: '', label: 'Select a preset…' },
                      ...PRESET_WATCHLISTS.map((p) => ({ value: p.key, label: p.label })),
                    ]}
                    className="w-full"
                    value={symbolUniverse.preset_key || ''}
                    onChange={(e) => handlePresetSelect(e.target.value)}
                  />
                </Field>
                {symbolUniverse.symbols.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap pt-0.5">
                    <span className="text-2xs font-mono text-txt-muted shrink-0">
                      {symbolUniverse.symbols.length} symbols
                    </span>
                    {symbolUniverse.symbols.slice(0, 6).map((sym) => (
                      <span key={sym} className="text-2xs font-mono px-1.5 py-0.5 rounded bg-bg-surface border border-bg-border text-txt-secondary">
                        {symbolShort(sym)}
                      </span>
                    ))}
                    {symbolUniverse.symbols.length > 6 && (
                      <span className="text-2xs font-mono text-txt-muted">+{symbolUniverse.symbols.length - 6} more</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Custom Watchlist */}
            {symbolUniverse.mode === 'CUSTOM_WATCHLIST' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-2xs font-mono text-txt-muted">
                    {symbolUniverse.symbols.length > 0
                      ? `${symbolUniverse.symbols.length} symbol${symbolUniverse.symbols.length !== 1 ? 's' : ''}${symbolUniverse.segment ? ` · ${symbolUniverse.segment}` : ''}`
                      : 'No symbols — all must be from the same segment'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSymbolPickerOpen(true)}
                    className="text-xs font-mono text-amber hover:text-amber/80 transition-colors flex items-center gap-1 shrink-0"
                  >
                    <Plus size={10} /> Add Symbol
                  </button>
                </div>
                {symbolUniverse.symbols.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {symbolUniverse.symbols.map((sym) => (
                      <span key={sym} className="flex items-center gap-1 text-2xs font-mono px-2 py-0.5 rounded-full bg-bg-surface border border-bg-border text-txt-secondary">
                        {symbolShort(sym)}
                        <button
                          type="button"
                          onClick={() => handleRemoveUniverseSymbol(sym)}
                          className="text-txt-muted hover:text-bear transition-colors ml-0.5 leading-none"
                          aria-label={`Remove ${sym}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Multi-symbol info banner */}
            {isMultiSymbol && (
              <p className="text-2xs font-mono text-info">
                Multi-symbol · {symbolCount} symbols — risk controls enforce percentage-based calculation only, no Option Premium.
              </p>
            )}
          </Section>
          )}

          {/* ── External Signal Source (EXTERNAL mode only) ── */}
          {strategySource === 'EXTERNAL' && (
            <ExternalSignalSourcePanel
              sourceType={externalSourceType}
              onSourceTypeChange={setExternalSourceType}
              externalExchange={externalExchange}
              onExternalExchangeChange={setExternalExchange}
              externalSegment={externalSegment}
              onExternalSegmentChange={setExternalSegment}
              externalBrokerName={externalBrokerName}
              onExternalBrokerNameChange={setExternalBrokerName}
              webhookUrl={webhookUrl}
              webhookToken={effectiveWebhookToken}
              tokenPending={isReservingToken}
              tokenError={tokenReservationError}
              onRetryToken={requestReservedStrategyToken}
              providerLocked={isGuidedCreate}
            />
          )}

          {/* ── Mobile TradingView guide trigger (EXTERNAL + TRADINGVIEW only).
              The desktop guide lives in the right column, which is hidden below
              lg — on mobile the same content opens in the shared BottomSheet. ── */}
          {isTvExternal && (
            <>
              <button
                type="button"
                className="lg:hidden w-full flex items-center justify-center gap-1.5 px-3 py-2 text-2xs font-mono rounded-lg border border-info/30 bg-info/10 text-info hover:bg-info/20"
                onClick={() => setTvGuideOpen(true)}
              >
                <Info size={11} />
                How to connect TradingView
              </button>
              <BottomSheet open={tvGuideOpen} onClose={() => setTvGuideOpen(false)} title="TradingView Setup Guide">
                <TradingViewGuideContent
                  webhookUrl={webhookUrl}
                  webhookToken={effectiveWebhookToken}
                  isOptionsByAlert={showTvOptionsNote}
                />
              </BottomSheet>
            </>
          )}

          {/* ── Market / Symbol Scope (MANUAL mode only) ── */}
          {strategySource === 'MANUAL' && (
          <Section title="Market / Symbol Scope" hint="Restricts symbols available in the Manual Trading watchlist" accent="bg-warn/50">
            <div className="grid gap-2 md:grid-cols-2">
              <Field label="Exchange" required>
                <FSelect
                  className="w-full"
                  options={EXTERNAL_EXCHANGE_OPTIONS}
                  value={manualExchange}
                  onChange={(e) => {
                    const ex = e.target.value;
                    setManualExchange(ex);
                    if (ex === 'MCX') setManualSegment('COMMODITY');
                    else if (manualSegment === 'COMMODITY') setManualSegment('STOCK');
                  }}
                />
              </Field>
              <Field label="Segment" required>
                <FSelect
                  className="w-full"
                  options={manualExchange === 'MCX'
                    ? EXTERNAL_SEGMENT_OPTIONS.filter((o) => o.value === 'COMMODITY')
                    : EXTERNAL_SEGMENT_OPTIONS.filter((o) => o.value !== 'COMMODITY')}
                  value={manualSegment}
                  onChange={(e) => setManualSegment(e.target.value)}
                />
              </Field>
            </div>
            <p className="text-2xs font-mono text-txt-muted">
              Manual strategies do not use Symbol Universe. Symbols are added per row in the Manual Trading watchlist after deployment.
            </p>
          </Section>
          )}

          {/* ── Execution Defaults (MANUAL mode only) ── */}
          {strategySource === 'MANUAL' && (
          <Section title="Execution Defaults" hint="Strategy-level defaults for manual signals" accent="bg-bull/70">
            <div className="grid gap-2 md:grid-cols-3">
              <Field label="Trade In">
                <FSelect
                  className="w-full"
                  options={[
                    { value: 'UNDERLYING', label: 'Underlying', disabled: manualSegment === 'INDEX' },
                    { value: 'OPTION', label: 'Option' },
                  ]}
                  value={manualTradeIn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setManualTradeIn(v);
                    if (v !== 'OPTION') setManualTradeInNextExp('OFF');
                  }}
                />
              </Field>
              <Field label="Product Type">
                <FSelect className="w-full" options={manualProductTypeOptions} value={manualProductType} onChange={(e) => setManualProductType(e.target.value)} />
              </Field>
              <Field label="Quantity Mode">
                <FSelect className="w-full" options={MANUAL_QTY_MODE_OPTIONS} value={manualQtyMode} onChange={(e) => setManualQtyMode(e.target.value)} />
              </Field>
              {manualTradeIn === 'OPTION' && (
                <Field label="Trade In Next Exp">
                  <FSelect
                    className="w-full"
                    options={TRADE_IN_NEXT_EXP_OPTIONS}
                    value={manualTradeInNextExp}
                    onChange={(e) => setManualTradeInNextExp(e.target.value)}
                  />
                </Field>
              )}
            </div>
            <label className="flex items-center gap-2 mt-1 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={manualPyramiding}
                onChange={(e) => setManualPyramiding(e.target.checked)}
                className="accent-amber w-3.5 h-3.5 shrink-0"
              />
              <span className="text-xs text-txt-secondary">Allow Pyramiding</span>
            </label>
            <p className="text-2xs font-mono text-txt-muted">
              Quantity value, Order Type, MPP, Risk Unit, Target and SL are set per symbol in the Manual Trading watchlist.
            </p>
          </Section>
          )}

          {/* ── Option Mapping (MANUAL + Trade In = Option) — reuses External legs editor ── */}
          {strategySource === 'MANUAL' && manualTradeIn === 'OPTION' && (
          <Section title="Option Mapping" hint="Resolve the executable option contract from the watchlist underlying" accent="bg-warn/70">
            {renderOptionLegsEditor({ hideQuantity: true })}
          </Section>
          )}

          {/* ── 3–6. Section condition editors — full-width stack (INTERNAL mode only) ── */}
          {strategySource === 'INTERNAL' && <div className="space-y-4">
            {SECTION_CONFIG.map(({ key, title, accent, hint }) => {
              const enabled  = watch(`sections.${key}.enabled`);
              const isEntry  = key === 'long_entry' || key === 'short_entry';
              const entryConfig = isEntry ? sectionEntryConfig[key] : null;
              const exec     = entryConfig?.execution || DEFAULT_ENTRY_EXECUTION;
              const lc       = entryConfig?.lifecycle  || DEFAULT_ENTRY_LIFECYCLE;
              const showRef  = exec.mode !== 'IMMEDIATE';

              return (
                <Section
                  key={key}
                  title={title}
                  hint={hint}
                  accent={accent}
                  compact
                  headerRight={
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <span className="text-2xs font-mono text-txt-muted">Enable</span>
                      <input
                        type="checkbox"
                        checked={!!enabled}
                        onChange={(e) => handleSectionEnable(key, e.target.checked)}
                        className="accent-amber w-3.5 h-3.5 shrink-0"
                      />
                    </label>
                  }
                >
                  {!enabled
                    ? <p className="text-2xs font-mono text-txt-muted">Section disabled.</p>
                    : isEntry
                    ? (
                      <div className="flex flex-col lg:flex-row gap-4">
                        {/* ── Left: editor ── */}
                        <div className="flex-1 min-w-0 space-y-3">
                          {/* Setup */}
                          <div className="space-y-1.5">
                            <p className="text-2xs font-mono text-txt-muted font-semibold uppercase tracking-wide">Setup</p>
                            <p className="text-2xs font-mono text-txt-muted/70">Setup is formed on closed candles only.</p>
                            <ConditionTreeEditor
                              tree={sectionTrees[key]}
                              onChange={(tree) => setSectionTrees((prev) => ({ ...prev, [key]: tree }))}
                              timeframe={primaryTimeframe}
                              catalogData={catalogQ.data}
                            />
                          </div>

                          {/* Execution + Lifecycle — 2-column on lg */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 border-t border-bg-border/40 pt-2.5">
                            {/* Execution block */}
                            <div className="space-y-2">
                              <p className="text-2xs font-mono text-txt-muted font-semibold uppercase tracking-wide">Execution</p>
                              <div className="flex flex-wrap gap-x-3 gap-y-2 items-end">
                                <div className="w-40">
                                  <EntrySelect
                                    label="Mode"
                                    value={exec.mode}
                                    options={ENTRY_EXECUTION_MODES}
                                    onChange={(v) => updateEntryConfig(key, 'execution', { mode: v })}
                                  />
                                </div>
                                {showRef && (
                                  <>
                                    <div className="w-36">
                                      <EntrySelect
                                        label="Reference"
                                        value={exec.reference}
                                        options={ENTRY_REFERENCE_OPTIONS}
                                        onChange={(v) => updateEntryConfig(key, 'execution', { reference: v })}
                                      />
                                    </div>
                                    {/* Separate Level only when NOT LTP_CROSS — for LTP_CROSS it's part of the grouped Trigger Level below */}
                                    {exec.mode !== 'LTP_CROSS' && (
                                      <div className="w-24">
                                        <EntrySelect
                                          label="Level"
                                          value={exec.level}
                                          options={ENTRY_LEVEL_OPTIONS}
                                          onChange={(v) => updateEntryConfig(key, 'execution', { level: v })}
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                                {/* LTP_CROSS: grouped Trigger Level — Level + Trigger Buffer + Unit in one inline row */}
                                {exec.mode === 'LTP_CROSS' && (
                                  <div>
                                    <div className="text-2xs font-mono text-txt-muted mb-0.5">Trigger Level</div>
                                    <div className="flex items-center gap-1.5">
                                      {/* Level select (no separate inner label) */}
                                      <div className="relative w-20">
                                        <select
                                          className="bg-bg-card border border-bg-border rounded px-2 py-1 text-2xs font-mono text-txt-primary appearance-none pr-5 cursor-pointer w-full"
                                          value={exec.level}
                                          onChange={(e) => updateEntryConfig(key, 'execution', { level: e.target.value })}
                                        >
                                          {ENTRY_LEVEL_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                          ))}
                                        </select>
                                        <svg className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-txt-muted" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                                          <path d="M5 7L1 3h8L5 7z" />
                                        </svg>
                                      </div>
                                      <span className="text-xs font-mono text-txt-muted/50 shrink-0">+</span>
                                      {/* Trigger buffer amount */}
                                      <input
                                        type="number" min="0" step="0.01"
                                        className="w-14 bg-bg-card border border-bg-border rounded px-2 py-1 text-2xs font-mono text-txt-primary"
                                        value={exec.buffer ?? 0}
                                        onChange={(e) => updateEntryConfig(key, 'execution', { buffer: Math.max(0, parseFloat(e.target.value) || 0) })}
                                      />
                                      {/* Unit toggle */}
                                      <div className="flex rounded overflow-hidden border border-bg-border text-2xs font-mono shrink-0">
                                        {[{ v: 'points', l: 'Points' }, { v: 'pct', l: '%' }].map(({ v, l }) => (
                                          <button
                                            key={v}
                                            type="button"
                                            onClick={() => updateEntryConfig(key, 'execution', { buffer_unit: v })}
                                            className={cn(
                                              'px-2 py-0.5 transition-colors',
                                              exec.buffer_unit === v ? 'bg-amber/15 text-amber' : 'text-txt-muted hover:bg-bg-hover'
                                            )}
                                          >{l}</button>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <p className="text-2xs font-mono text-txt-muted/70">
                                {exec.mode === 'IMMEDIATE'         && 'Enter as soon as setup is formed.'}
                                {exec.mode === 'NEXT_CANDLE_CLOSE' && 'Wait for candle close confirmation against the selected reference level.'}
                                {exec.mode === 'LTP_CROSS'         && 'Enter when real-time LTP crosses the selected reference level.'}
                                {exec.mode === 'CROSS_AND_CLOSE'   && 'Require intrabar cross first, then candle close confirmation.'}
                              </p>
                              {/* Trigger preview — always visible when LTP_CROSS, buffer part omitted when 0 */}
                              {exec.mode === 'LTP_CROSS' && (() => {
                                const dir     = exec.level === 'LOW' ? 'BELOW' : 'ABOVE';
                                const refLbl  = exec.reference === 'SETUP_CANDLE' ? 'Setup Candle' : 'Previous Candle';
                                const lvlLbl  = { HIGH: 'High', LOW: 'Low', CLOSE: 'Close' }[exec.level] || exec.level;
                                const bufPart = (exec.buffer ?? 0) > 0
                                  ? ` + ${exec.buffer}${exec.buffer_unit === 'pct' ? '%' : ' pts'}`
                                  : '';
                                return (
                                  <p className="text-2xs font-mono text-info/80">
                                    {`Trigger: LTP crosses ${dir} ${refLbl} ${lvlLbl}${bufPart}`}
                                  </p>
                                );
                              })()}
                            </div>

                            {/* Lifecycle block */}
                            <div className="space-y-2">
                              <p className="text-2xs font-mono text-txt-muted font-semibold uppercase tracking-wide">Lifecycle</p>
                              <div className="flex flex-wrap gap-x-3 gap-y-2 items-end">
                                <div className="w-40">
                                  <EntrySelect
                                    label="Validity"
                                    value={lc.validity}
                                    options={ENTRY_VALIDITY_OPTIONS}
                                    onChange={(v) => updateEntryConfig(key, 'lifecycle', { validity: v })}
                                  />
                                </div>
                                {lc.validity === 'EXPIRE_AFTER_N_CANDLES' && (
                                  <div>
                                    <div className="text-2xs font-mono text-txt-muted mb-0.5">Candles</div>
                                    <input
                                      type="number"
                                      min="1"
                                      className="w-16 bg-bg-card border border-bg-border rounded px-2 py-1 text-2xs font-mono text-txt-primary"
                                      value={lc.expiry_candles}
                                      onChange={(e) => updateEntryConfig(key, 'lifecycle', {
                                        expiry_candles: Math.max(1, parseInt(e.target.value, 10) || 3),
                                      })}
                                    />
                                  </div>
                                )}
                                <div className="w-40">
                                  <EntrySelect
                                    label="Replacement"
                                    value={lc.replacement}
                                    options={ENTRY_REPLACEMENT_OPTIONS}
                                    onChange={(v) => updateEntryConfig(key, 'lifecycle', { replacement: v })}
                                  />
                                </div>
                              </div>
                              <p className="text-2xs font-mono text-txt-muted/70">
                                {lc.validity === 'UNTIL_TRIGGER'          && 'Setup stays active until entry fires or is replaced.'}
                                {lc.validity === 'EXPIRE_AFTER_N_CANDLES' && 'Setup becomes invalid if entry does not occur within N candles.'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* ── Right: summary panel ── */}
                        <div className="lg:w-[280px] shrink-0 lg:sticky lg:top-4 self-start">
                          <EntrySummaryPanel
                            exec={exec}
                            lc={lc}
                            tree={sectionTrees[key]}
                            timeframe={primaryTimeframe}
                            sectionKey={key}
                          />
                        </div>
                      </div>
                    )
                    : (
                      <ConditionTreeEditor
                        tree={sectionTrees[key]}
                        onChange={(tree) => setSectionTrees((prev) => ({ ...prev, [key]: tree }))}
                        timeframe={primaryTimeframe}
                        catalogData={catalogQ.data}
                      />
                    )
                  }
                </Section>
              );
            })}
          </div>}

          {/* ── 6. Time Rules ── */}
          <Section title="Time Rules" hint="Trading session time windows" accent="bg-info/50">
            <p className="text-2xs font-mono text-txt-muted -mt-1">
              Exits may still fire even when new entries are blocked by a time window.
            </p>
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">

              {/* Trading Mode */}
              <div>
                <div className="field-label mb-1">Trading Mode</div>
                <Controller
                  name="global_rules.trading_mode"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      className="w-32"
                      options={[
                        { value: 'INTRADAY',   label: 'Intraday'   },
                        { value: 'POSITIONAL', label: 'Positional' },
                      ]}
                      {...field}
                    />
                  )}
                />
              </div>

              {/* Divider: mode → times */}
              <div className="w-px h-7 bg-bg-border/60 self-end mb-0.5 shrink-0" />

              {/* Start Time */}
              <div>
                <div className="field-label mb-1">Start Time</div>
                <FInput type="time" className="w-28" {...register('global_rules.start_time')} />
                {errors?.global_rules?.start_time?.message && (
                  <p className="text-2xs font-mono text-bear mt-0.5">{errors.global_rules.start_time.message}</p>
                )}
              </div>

              {/* Entry Till */}
              <div>
                <div className="field-label mb-1">Entry Till</div>
                <FInput type="time" className="w-28" {...register('global_rules.no_entry_after')} />
                {errors?.global_rules?.no_entry_after?.message && (
                  <p className="text-2xs font-mono text-bear mt-0.5">{errors.global_rules.no_entry_after.message}</p>
                )}
              </div>

              {/* End Time */}
              <div>
                <div className="field-label mb-1">End Time</div>
                <FInput type="time" className="w-28" {...register('global_rules.end_time')} />
                {errors?.global_rules?.end_time?.message && (
                  <p className="text-2xs font-mono text-bear mt-0.5">{errors.global_rules.end_time.message}</p>
                )}
              </div>

            </div>
          </Section>

          {/* ── 7. Execution ── */}
          {strategySource !== 'MANUAL' && (
          <Section title="Execution" hint="How signals are executed" accent="bg-bull/70">
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">

              {/* Trade In */}
              <div>
                <div className="field-label mb-1">Trade In</div>
                <Controller
                  name="execution.instrument"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      className="w-32"
                      options={[
                        { value: 'UNDERLYING', label: 'Underlying' },
                        { value: 'OPTIONS',    label: 'Options', disabled: isMultiSymbol || isSymbolOption },
                      ]}
                      {...field}
                    />
                  )}
                />
              </div>

              {/* Trade In Next Exp — options only */}
              {isOptions && (
                <div>
                  <div className="field-label mb-1">Trade In Next Exp</div>
                  <Controller
                    name="execution.trade_in_next_exp"
                    control={control}
                    render={({ field }) => (
                      <FSelect
                        className="w-44"
                        options={TRADE_IN_NEXT_EXP_OPTIONS}
                        {...field}
                      />
                    )}
                  />
                </div>
              )}

              {/* Divider: trade-in → product */}
              <div className="w-px h-7 bg-bg-border/60 self-end mb-0.5 shrink-0" />

              {/* Product Type */}
              <div>
                <div className="field-label mb-1">Product Type</div>
                <Controller
                  name="execution.product_type"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      className="w-32"
                      options={productTypeOptions}
                      name={field.name}
                      value={field.value || (isOptions ? 'MARGIN' : 'INTRADAY')}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue('execution.product_type', e.target.value, { shouldDirty: true, shouldValidate: true });
                      }}
                    />
                  )}
                />
              </div>

              {/* Divider: product → signal (or long/short) */}
              <div className="w-px h-7 bg-bg-border/60 self-end mb-0.5 shrink-0" />

              {/* Opposite Signal Handling — hidden for EXTERNAL strategies */}
              {strategySource !== 'EXTERNAL' && (
                <>
                  <div>
                    <div className="field-label mb-1">Opp. Signal</div>
                    <Controller
                      name="global_rules.opposite_signal_handling"
                      control={control}
                      render={({ field }) => (
                        <FSelect
                          className="w-40"
                          options={[
                            { value: 'EXIT_AND_REVERSE', label: 'Exit & Reverse' },
                            { value: 'WAIT_FOR_EXIT',    label: 'Wait for Exit'  },
                          ]}
                          name={field.name}
                          value={field.value || 'EXIT_AND_REVERSE'}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            field.onChange(nextValue);
                            setValue('global_rules.opposite_signal_handling', nextValue, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                        />
                      )}
                    />
                  </div>

                  {/* Divider: signal → long/short */}
                  <div className="w-px h-7 bg-bg-border/60 self-end mb-0.5 shrink-0" />
                </>
              )}

              {/* Allow Long */}
              {(() => {
                const checked = !!watch('global_rules.allow_long');
                return (
                  <div>
                    <div className="field-label mb-1">Allow Long</div>
                    <label className={cn(
                      'flex items-center gap-1.5 h-8 px-2.5 rounded border cursor-pointer transition-colors',
                      checked ? 'border-bull/30 bg-bull/5' : 'border-bg-border bg-bg-surface/40 hover:border-bg-hover'
                    )}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setValue('global_rules.allow_long', e.target.checked, { shouldDirty: true })}
                        className="accent-amber w-3.5 h-3.5 shrink-0"
                      />
                      <span className={cn('text-xs font-medium', checked ? 'text-txt-primary' : 'text-txt-muted')}>
                        {checked ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {/* Allow Short */}
              {(() => {
                const checked = !!watch('global_rules.allow_short');
                return (
                  <div>
                    <div className="field-label mb-1">Allow Short</div>
                    <label className={cn(
                      'flex items-center gap-1.5 h-8 px-2.5 rounded border cursor-pointer transition-colors',
                      checked ? 'border-bear/30 bg-bear/5' : 'border-bg-border bg-bg-surface/40 hover:border-bg-hover'
                    )}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setValue('global_rules.allow_short', e.target.checked, { shouldDirty: true })}
                        className="accent-amber w-3.5 h-3.5 shrink-0"
                      />
                      <span className={cn('text-xs font-medium', checked ? 'text-txt-primary' : 'text-txt-muted')}>
                        {checked ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {/* Divider: long/short → pyramiding */}
              <div className="w-px h-7 bg-bg-border/60 self-end mb-0.5 shrink-0" />

              {/* Pyramiding */}
              {(() => {
                const pyramidingEnabled = isPyramidingToggleEnabled({ tradeIn });
                const checked = pyramidingEnabled && !!watch('execution.allow_pyramiding');
                return (
                  <div>
                    <div className="field-label mb-1">Pyramiding</div>
                    <label className={cn(
                      'flex items-center gap-1.5 h-8 px-2.5 rounded border transition-colors',
                      !pyramidingEnabled
                        ? 'cursor-not-allowed border-bg-border bg-bg-surface/30 opacity-60'
                        : (checked ? 'cursor-pointer border-info/30 bg-info/5' : 'cursor-pointer border-bg-border bg-bg-surface/40 hover:border-bg-hover')
                    )}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!pyramidingEnabled}
                        onChange={(e) => setValue('execution.allow_pyramiding', e.target.checked, { shouldDirty: true })}
                        className="accent-amber w-3.5 h-3.5 shrink-0"
                      />
                      <span className={cn('text-xs font-medium', checked ? 'text-txt-primary' : 'text-txt-muted')}>
                        {checked ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {!isOptions && watch('execution.allow_pyramiding') && (
                <div>
                  <div className="field-label mb-1">Max Pyramid Entries</div>
                  <FInput
                    type="number"
                    step="1"
                    min="1"
                    max="20"
                    className="w-32"
                    {...register('execution.max_pyramid_legs', { valueAsNumber: true })}
                  />
                  <p className="text-2xs font-mono text-txt-muted mt-0.5 max-w-56">
                    Maximum number of open stacked entries allowed for UNDERLYING pyramiding
                  </p>
                  {errors?.execution?.max_pyramid_legs?.message && (
                    <p className="text-2xs font-mono text-bear mt-0.5">{errors.execution.max_pyramid_legs.message}</p>
                  )}
                </div>
              )}

              {/* Partial Exit */}
              {(() => {
                const checked = !!watch('execution.allow_partial_exit');
                const pyramidingOn = !!watch('execution.allow_pyramiding');
                const disabled = !isPartialExitToggleEnabled({ quantityMode, allowPyramiding: pyramidingOn });
                return (
                  <div>
                    <div className="field-label mb-1">Partial Exit</div>
                    <label className={cn(
                      'flex items-center gap-1.5 h-8 px-2.5 rounded border transition-colors',
                      disabled
                        ? 'cursor-not-allowed border-bg-border bg-bg-surface/30 opacity-60'
                        : (checked ? 'cursor-pointer border-info/30 bg-info/5' : 'cursor-pointer border-bg-border bg-bg-surface/40 hover:border-bg-hover')
                    )}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => setValue('execution.allow_partial_exit', e.target.checked, { shouldDirty: true })}
                        className="accent-amber w-3.5 h-3.5 shrink-0"
                      />
                      <span className={cn('text-xs font-medium', checked ? 'text-txt-primary' : 'text-txt-muted')}>
                        {checked ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                );
              })()}

            </div>

            <div className="flex flex-wrap items-end gap-x-3 gap-y-2 pt-2 border-t border-bg-border/40 mt-2">
              <div>
                <div className="field-label mb-1">Quantity Mode</div>
                <Controller
                  name="execution.quantity.mode"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      className="w-36"
                      options={[
                        { value: 'FIXED_QTY', label: 'Fixed Qty' },
                        { value: 'BY_MARGIN', label: 'By Margin' },
                        { value: 'BY_RISK', label: 'By Risk' },
                        ...(strategySource === 'EXTERNAL' ? [{ value: 'BY_ALERT', label: 'By Alert' }] : []),
                      ]}
                      name={field.name}
                      value={field.value || 'FIXED_QTY'}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      onChange={(e) => {
                        const next = e.target.value;
                        field.onChange(next);
                        setValue('execution.quantity.mode', next, { shouldDirty: true, shouldValidate: true });
                        if (next !== 'FIXED_QTY') setValue('execution.quantity.fixed_qty', null, { shouldDirty: true });
                        if (next !== 'BY_MARGIN') setValue('execution.quantity.margin_per_trade', null, { shouldDirty: true });
                        if (next !== 'BY_RISK') setValue('execution.quantity.risk_amount', null, { shouldDirty: true });
                      }}
                    />
                  )}
                />
              </div>

              {quantityMode === 'FIXED_QTY' && (
                <div>
                  <div className="field-label mb-1">QTY</div>
                  <FInput
                    type="number"
                    step="1"
                    min="1"
                    className="w-28"
                    {...register('execution.quantity.fixed_qty', { valueAsNumber: true })}
                  />
                  {errors?.execution?.quantity?.fixed_qty?.message && (
                    <p className="text-2xs font-mono text-bear mt-0.5">{errors.execution.quantity.fixed_qty.message}</p>
                  )}
                </div>
              )}

              {quantityMode === 'BY_MARGIN' && (
                <div>
                  <div className="field-label mb-1">Margin / Trade</div>
                  <FInput
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-28"
                    {...register('execution.quantity.margin_per_trade', { valueAsNumber: true })}
                  />
                  {errors?.execution?.quantity?.margin_per_trade?.message && (
                    <p className="text-2xs font-mono text-bear mt-0.5">{errors.execution.quantity.margin_per_trade.message}</p>
                  )}
                </div>
              )}

              {quantityMode === 'BY_RISK' && (
                <div>
                  <div className="field-label mb-1">Risk Amount</div>
                  <FInput
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-28"
                    {...register('execution.quantity.risk_amount', { valueAsNumber: true })}
                  />
                  {errors?.execution?.quantity?.risk_amount?.message && (
                    <p className="text-2xs font-mono text-bear mt-0.5">{errors.execution.quantity.risk_amount.message}</p>
                  )}
                </div>
              )}

              {quantityMode === 'BY_ALERT' && strategySource !== 'EXTERNAL' && (
                <p className="text-2xs font-mono text-txt-muted self-end pb-2">
                  Incoming external alert quantity will be used at runtime.
                </p>
              )}

              <div>
                <div className="field-label mb-1">Order Type</div>
                <Controller
                  name="execution.entry_order.order_type"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      className="w-28"
                      options={[
                        { value: 'MARKET', label: 'Market' },
                        { value: 'LIMIT', label: 'Limit' },
                        { value: 'SL-M', label: 'SL-M' },
                        { value: 'SL-L', label: 'SL-L' },
                      ]}
                      name={field.name}
                      value={field.value || 'MARKET'}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue('execution.entry_order.order_type', e.target.value, { shouldDirty: true, shouldValidate: true });
                      }}
                    />
                  )}
                />
              </div>

              {(entryOrderType === 'MARKET' || entryOrderType === 'LIMIT') && (
                <div>
                  <div className="field-label mb-1">MPP (%)</div>
                  <Controller
                    name="execution.entry_order.mpp"
                    control={control}
                    render={({ field }) => (
                      <FInput
                        type="number"
                        className="w-20"
                        min={0}
                        max={100}
                        step="any"
                        placeholder="2"
                        {...field}
                        value={field.value ?? 2}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          field.onChange(isNaN(v) ? 2 : v);
                        }}
                      />
                    )}
                  />
                </div>
              )}

              {/* Auto Slice */}
              <div className="flex items-center gap-2 pt-5">
                <input
                  type="checkbox"
                  id="auto_slice"
                  {...register('execution.entry_order.auto_slice')}
                  className="accent-brand w-4 h-4"
                />
                <label htmlFor="auto_slice" className="field-label cursor-pointer">AUTO SLICE</label>
              </div>

              {showLimitPriceMode && (
                <div>
                  <div className="field-label mb-1">Limit Price Mode</div>
                  <Controller
                    name="execution.entry_order.price_mode"
                    control={control}
                    render={({ field }) => (
                      <FSelect
                        className="w-40"
                        options={[
                          { value: 'OFFSET_POINTS', label: 'Offset Points' },
                          { value: 'OFFSET_PERCENTAGE', label: 'Offset Percentage' },
                          ...legacyLimitModeOptions,
                        ]}
                        name={field.name}
                        value={field.value || 'OFFSET_POINTS'}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setValue('execution.entry_order.price_mode', e.target.value, { shouldDirty: true, shouldValidate: true });
                        }}
                      />
                    )}
                  />
                  {errors?.execution?.entry_order?.price_mode?.message && (
                    <p className="text-2xs font-mono text-bear mt-0.5">{errors.execution.entry_order.price_mode.message}</p>
                  )}
                </div>
              )}

              {showLimitPriceValue && (
                <div>
                  <div className="field-label mb-1">Limit Price</div>
                  <FInput
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-28"
                    {...register('execution.entry_order.limit_price_value', { valueAsNumber: true })}
                  />
                  {errors?.execution?.entry_order?.limit_price_value?.message && (
                    <p className="text-2xs font-mono text-bear mt-0.5">{errors.execution.entry_order.limit_price_value.message}</p>
                  )}
                </div>
              )}
            </div>

            {entryOrderType === 'SL-M' && (
              <p className="text-2xs font-mono text-txt-muted mt-1">
                Trigger price is taken automatically from the incoming signal, alert, or execution reference price.
              </p>
            )}
            {entryOrderType === 'SL-L' && (
              <p className="text-2xs font-mono text-txt-muted mt-1">
                Trigger price is taken automatically from the incoming signal, alert, or execution reference price. Only the limit leg is configured by offset.
              </p>
            )}

            <p className="text-2xs font-mono text-txt-muted mt-1">
              NSE/BSE F&O and MCX quantities are treated as lots through the existing runtime lot-size path.
            </p>

            {/* Index + UNDERLYING warning */}
            {tradeIn === 'UNDERLYING' && isIndexSelection && (
              <p className="text-2xs font-mono text-warn mt-1">
                Note: Index symbols cannot be traded as UNDERLYING. Consider switching to OPTIONS.
              </p>
            )}

            {/* Option Mapping */}
            {tradeIn === 'OPTIONS' && isSymbolOption && (
              <p className="text-2xs font-mono text-warn mt-1">
                Selected symbol is already an option instrument. Option leg configuration is not applicable.
              </p>
            )}
            {tradeIn === 'OPTIONS' && !isSymbolOption && renderOptionLegsEditor()}
          </Section>
          )}

          {/* ── 8. Target / SL / TSL — hidden for EXTERNAL Simple mode & MANUAL ── */}
          {strategySource !== 'MANUAL' && !(strategySource === 'EXTERNAL' && externalMode === 'SIMPLE') && (
          <Section title="Target / SL / TSL" hint="Risk management controls" accent="bg-bear/70">
            {/* Contextual banners */}
            {isMultiSymbol && (
              <p className="text-2xs font-mono text-info -mt-1">
                Only percentage-based risk is supported for multi-symbol strategies.
              </p>
            )}
            {isMultiLeg && isOptions && !isMultiSymbol && (
              <p className="text-2xs font-mono text-info -mt-1">
                Option Premium is supported only for single-leg option strategies.
              </p>
            )}

            {/* All controls in one row: Apply On · Unit · Target · Stop Loss · Trailing Stop */}
            <div className="flex flex-wrap items-end gap-x-5 gap-y-3">

              {/* Apply On */}
              <div>
                <div className="field-label mb-1">Apply On</div>
                <Controller
                  name="risk.target.apply_on"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      options={getApplyOnOptions(isOptions, isMultiLeg, isMultiSymbol)}
                      className="w-36"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('risk.stop_loss.apply_on',     e.target.value, { shouldDirty: true });
                        setValue('risk.trailing_stop.apply_on', e.target.value, { shouldDirty: true });
                      }}
                    />
                  )}
                />
              </div>

              {/* Unit */}
              <div>
                <div className="field-label mb-1">Unit</div>
                <Controller
                  name="risk.target.type"
                  control={control}
                  render={({ field }) => (
                    <FSelect
                      options={[
                        { value: 'percent', label: '%'   },
                        { value: 'points',  label: 'pts', disabled: isMultiSymbol },
                      ]}
                      className="w-20"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('risk.stop_loss.type',    e.target.value, { shouldDirty: true });
                        setValue('risk.trailing_stop.type', e.target.value, { shouldDirty: true });
                      }}
                    />
                  )}
                />
              </div>

              {/* Target · Stop Loss · Trailing Stop */}
              {RISK_ROWS.map(({ key, label }) => {
                const val     = Number(watch(`risk.${key}.value`)) || 0;
                const enabled = val > 0;
                return (
                  <div key={key}>
                    <div className="field-label mb-1">{label}</div>
                    <FInput
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`risk.${key}.value`)}
                      placeholder="0"
                      className={cn(
                        'w-20 transition-colors',
                        enabled
                          ? 'border-amber/40 bg-amber/5 text-txt-primary'
                          : 'border-bg-border text-txt-muted',
                        errors?.risk?.[key]?.value && 'border-bear/60 ring-1 ring-bear/40'
                      )}
                    />
                    {errors?.risk?.[key]?.value?.message && (
                      <p className="text-2xs font-mono text-bear mt-0.5">{errors.risk[key].value.message}</p>
                    )}
                  </div>
                );
              })}

            </div>
          </Section>
          )}

          {/* Clearance for the fixed mobile Save/Cancel bar */}
          <div className="h-20 lg:hidden" aria-hidden="true" />
        </form>

        {/* ── Right: sticky preview (desktop only) ── */}
        <div className="hidden lg:block w-[360px] shrink-0">
          <div className="sticky top-20 space-y-4">
            <StrategyPreviewPanel
              formValues={allFormValues}
              sectionTrees={sectionTrees}
              optionMapping={optionMapping}
              strategySource={strategySource}
              externalSourceType={externalSourceType}
              manual={{ exchange: manualExchange, segment: manualSegment, tradeIn: manualTradeIn, qtyMode: manualQtyMode }}
            />
            {/* TradingView Setup Guide — adapts the right-side area for
                EXTERNAL + TRADINGVIEW only (mobile uses the BottomSheet). */}
            {isTvExternal && (
              <div className="card p-4">
                <h3 className="text-xs font-semibold text-txt-primary mb-2">TradingView Setup Guide</h3>
                <TradingViewGuideContent
                  webhookUrl={webhookUrl}
                  webhookToken={effectiveWebhookToken}
                  isOptionsByAlert={showTvOptionsNote}
                />
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Mobile sticky Save/Cancel bar (replaces the hidden global bottom nav below lg).
          Uses `sticky`, not `fixed`: AppShell's `.animate-fade-in` wrapper keeps a permanent
          identity transform after its mount animation (fill-mode: forwards), which makes it
          the containing block for `position: fixed` descendants — a `fixed` bar here would
          render at the transformed ancestor's offset instead of the true viewport. `sticky`
          is anchored to the nearest scroll container (`<main>`) instead, so it isn't affected. ── */}
      <div className="lg:hidden sticky bottom-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 flex items-center gap-2 py-3 border-t border-bg-border bg-bg-base/95 backdrop-blur-sm safe-area-pb">
        <button
          type="button"
          onClick={() => navigate(ROUTES.STRATEGIES)}
          className="btn-ghost text-xs"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="strategy-form"
          disabled={busy || editLifecycleBlocked}
          className="btn-primary text-xs flex-1 justify-center flex items-center gap-1.5"
        >
          {busy ? <Spinner size={12} className="text-bg-base" /> : <Zap size={11} />}
          {busy ? 'Saving…' : isEdit ? 'Update Strategy' : 'Save Strategy'}
        </button>
      </div>
    </>
  );
}
